import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// --- ICONS ---
import {
    Target, Zap, Film, BarChart2, Clapperboard,
    Megaphone, Settings, Plus, Trash2, Edit, Copy, TrafficCone,
    ArrowRight, ArrowLeft, Video, UploadCloud, FileText, Lightbulb, TrendingUp, Users,
    Palette, Heart, Info, Camera, Check, CheckCircle, XCircle, AlertTriangle, Briefcase, Download, Trophy, Star, Newspaper, MessageSquare, Handshake, ThumbsUp, ThumbsDown, DollarSign,
    PieChart as PieChartIcon, Sliders, MapPin, Send, MessageCircle, UserCog, Menu,
    X, LogOut, BrainCircuit, Banknote, ExternalLink, ChevronsUpDown, Share2, ChevronDown, CopyCheck as CopyCheckIcon,
    Instagram, Twitter, Facebook, Youtube, Radar, Sparkles, ShieldQuestion, HelpCircle, ChevronUp, Box, PenSquare, AreaChart, ChevronsLeft, ChevronsRight, Database, WandSparkles, Calendar, Save, ImageIcon, Minimize, Maximize
} from 'lucide-react';

// --- FIREBASE IMPORTS ---
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, onSnapshot, collection, doc, getDoc, setDoc, getDocs, query, where, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, getStorage, getDownloadURL, uploadBytes } from 'firebase/storage';
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom';


import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import PublicDossierPage from './features/Dossier/PublicDossierPage';
import LandingSite from './features/Landing/LandingSite';
import GreenlightCoverageTab from './features/Greenlight/GreenlightCoverageTab'; 

// --- CONFIG & HOOKS ---
import { firebaseConfig } from './config/firebase'; 
import { useCreditSystem } from './hooks/useCreditSystem';

// --- AI SERVICES ---
import { generateInfluencerListLLM } from './services/aiService';

// --- FIREBASE UTILITIES ---
import { uploadImageToFirebase } from './services/firebaseUtils';

// --- SHARED UI COMPONENTS ---
import { 
    AppCard, 
    AppButton, 
    BlackboxLoaderIcon, 
    Notification,
    AppModal,
    BlackboxIcon
} from './components/SharedUI';

// --- ONBOARDING ---
import { OnboardingFlow } from './Onboarding'; 

// --- FEATURE TABS ---
import AudienceAnalysisTab from './features/Audience/AudienceAnalysisTab';
import RevenueForecasterTab from './features/Revenue/RevenueForecasterTab';
import VideoAnalysisTab from './features/Video/VideoAnalysisTab';
import VisualContentTab from './features/Visuals/VisualContentTab';
import ContentCreationTab from './features/Content/ContentCreationTab';
import BudgetPlannerTab from './features/Budget/BudgetPlannerTab';
import CampaignManagerTab from './features/Campaigns/CampaignManagerTab';
import AndromedaSimulatorTab from './features/Campaigns/AndromedaSimulatorTab';
import CompetitiveIntelTab from './features/Competition/CompetitiveIntelTab';
import PressOutreachTab from './features/Press/PressOutreachTab';
import AnalyticsTab from './features/Analytics/AnalyticsTab';
import CampaignIntelligenceTab from './features/Community/CampaignIntelligenceTab';
import RemixTab from './features/Remix/RemixTab';
import DossierTab from './features/Dossier/DossierTab';
import SettingsTab from './features/Settings/SettingsTab';
import ProjectDashboard from './pages/ProjectDashboard';

// --- INITIALIZE FIREBASE ---
// Initialize only if not already initialized
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const CreditPricingModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const pricingTiers = [
    {
      category: "Core Strategy",
      items: [
        { name: "Audience Genius Analysis", cost: 5 },
        { name: "Revenue Forecast Report", cost: 5 },
        { name: "Budget Planner", cost: 3 },
        { name: "Competitive Intel Deep-Dive", cost: 2 },
      ]
    },
    {
      category: "Creative & Visuals",
      items: [
        { name: "Poster A/B Analysis", cost: 3 },
        { name: "Video/Script Analysis", cost: 5 },
        { name: "Ad Concept Generation", cost: 2 },
        { name: "Tagline Generation", cost: 1 },
      ]
    },
    {
      category: "Press & Outreach",
      items: [
        { name: "Full Press Strategy", cost: 5 },
        { name: "Influencer Discovery", cost: 2 },
        { name: "Press Kit Bio Generation", cost: 1 },
      ]
    },
    {
      category: "Social Remix",
      items: [
        { name: "Comment Analysis", cost: "1 / img" },
        { name: "Remix Graphic Generation", cost: 3 },
      ]
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-2xl w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white font-poppins flex items-center gap-2">
            <Zap className="w-5 h-5 text-purple-500" />
            Credit Rate Card
          </h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pricingTiers.map((tier, index) => (
            <div key={index} className="bg-zinc-800/30 rounded-lg p-4 border border-zinc-800">
              <h4 className="font-semibold text-purple-300 mb-3 text-sm uppercase tracking-wider">
                {tier.category}
              </h4>
              <ul className="space-y-2">
                {tier.items.map((item, i) => (
                  <li key={i} className="flex justify-between items-center text-sm">
                    <span className="text-zinc-300">{item.name}</span>
                    <span className="font-mono font-bold text-white bg-zinc-700 px-2 py-0.5 rounded text-xs">
                      {item.cost}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-4 border-t border-zinc-800 text-center">
          <p className="text-xs text-zinc-500">
            Credits reset monthly on your billing date. Unused credits do not roll over.
          </p>
        </div>
      </div>
    </div>
  );
};

const RefillModal = ({ isOpen, onClose, onConfirm, isProcessing }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="text-center mb-6">
            <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white font-poppins">Studio Refill Pack</h3>
            <p className="text-zinc-400 text-sm mt-2">
                Running low? Add credits instantly without changing your subscription plan.
            </p>
        </div>

        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 mb-6 flex justify-between items-center">
            <div>
                <p className="font-bold text-white text-lg">10 Credits</p>
                <p className="text-xs text-zinc-500">One-time purchase</p>
            </div>
            <p className="font-mono text-xl font-bold text-white">$49</p>
        </div>

        <div className="space-y-3">
            <button
                onClick={onConfirm}
                disabled={isProcessing}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold rounded-lg transition-all shadow-lg shadow-purple-500/20 flex justify-center items-center gap-2"
            >
                {isProcessing ? (
                    <>Processing...</>
                ) : (
                    <>
                        <Zap size={18} fill="currentColor" />
                        Top Up Now
                    </>
                )}
            </button>
            <button onClick={onClose} disabled={isProcessing} className="w-full py-2 text-sm text-zinc-500 hover:text-white">
                Cancel
            </button>
        </div>
        
        <p className="text-[10px] text-zinc-600 text-center mt-4">
            Secure payment via Stripe. Credits do not roll over.
        </p>
      </div>
    </div>
  );
};

const ProjectContextBar = ({ filmData, activeTab, allTabDetails, tabs, onSwitchProject }) => {
    if (!filmData) return null;

    // Find parent group for current tab (for breadcrumb)
    const groupEntry = Object.entries(tabs).find(([, group]) =>
        Array.isArray(group.children) && group.children.includes(activeTab)
    );
    const groupLabel = groupEntry?.[1]?.label ?? null;
    const tabDetail = allTabDetails[activeTab];

    const GENRE_STYLES = {
        'Action':   'bg-red-500/15 text-red-300 border-red-500/25',
        'Drama':    'bg-blue-500/15 text-blue-300 border-blue-500/25',
        'Horror':   'bg-orange-500/15 text-orange-300 border-orange-500/25',
        'Comedy':   'bg-yellow-500/15 text-yellow-300 border-yellow-500/25',
        'Thriller': 'bg-purple-500/15 text-purple-300 border-purple-500/25',
        'Sci-Fi':   'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
        'Romance':  'bg-pink-500/15 text-pink-300 border-pink-500/25',
        'Documentary': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
    };
    const genreStyle = GENRE_STYLES[filmData.genre] ?? 'bg-zinc-800/60 text-zinc-400 border-zinc-700';

    return (
        <div className="flex items-end justify-between mb-6 lg:mb-8 gap-4 min-w-0 pb-5 border-b border-white/5">
            {/* LEFT: Breadcrumb → Tab Title */}
            <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2 mb-1.5 h-4">
                    {groupLabel ? (
                        <>
                            <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-[0.18em]">
                                {groupLabel}
                            </span>
                            <span className="text-zinc-700 text-xs">›</span>
                            <span className="text-[10px] font-bold text-purple-500/70 uppercase tracking-[0.18em]">
                                {tabDetail?.label}
                            </span>
                        </>
                    ) : (
                        <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-[0.18em] opacity-0 select-none">
                            &nbsp;
                        </span>
                    )}
                </div>
                <h2 className="text-2xl lg:text-[2.25rem] tracking-tight text-white font-poppins-light-italic leading-none truncate">
                    {tabDetail?.label ?? 'Dashboard'}
                </h2>
            </div>

            {/* RIGHT: Active Project Chip — hidden on mobile */}
            {filmData.title && (
                <div className="hidden md:flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-3 bg-zinc-900/70 border border-white/[0.07] rounded-xl px-3 py-2 hover:border-purple-500/30 transition-colors duration-200 cursor-pointer group" onClick={onSwitchProject}>
                        {/* Film Poster Thumbnail */}
                        <div className="relative w-7 h-10 rounded overflow-hidden flex-shrink-0 bg-zinc-800">
                            {filmData.poster_a ? (
                        <img
                            src={filmData.poster_a}
                            alt={filmData.title}
                            className="w-full h-full object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <Film size={11} className="text-zinc-600" />
                        </div>
                    )}
                            {/* Subtle shine overlay */}
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none" />
                        </div>

                        {/* Film Info */}
                        <div className="min-w-0 pr-1">
                            <p className="text-xs font-bold text-white truncate max-w-[160px] group-hover:text-purple-200 transition-colors">
                                {filmData.title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                {filmData.genre && (
                                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${genreStyle}`}>
                                        {filmData.genre}
                                    </span>
                                )}
                                {filmData.budgetLevel && (
                                    <span className="text-[9px] text-zinc-600 truncate max-w-[90px]">
                                        {filmData.budgetLevel.split(' ')[0]}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Switch arrow */}
                        <ChevronsUpDown size={12} className="text-zinc-700 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                    </div>
                </div>
            )}
        </div>
    );
};


// --- COMPONENT: USAGE METER ---
const UsageMeter = ({ remaining, limit, loading, isPro, onUpgrade, onRefill, onShowPricing }) => {
  if (loading) return <div className="h-10 animate-pulse bg-zinc-800 rounded-lg mx-4 mb-4"></div>;

  const percentage = Math.min(100, Math.max(0, (remaining / limit) * 100));
  
  // Visual tweaks: Orange warning starts at 40%, Red danger at 20%
  const barColor = percentage > 40 ? 'bg-purple-600' : percentage > 20 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="px-4 mb-6">
      <div className="bg-zinc-800/80 border border-zinc-700 rounded-xl p-4 shadow-lg relative">
        {/* Help Button */}
        <button 
            onClick={(e) => { e.stopPropagation(); onShowPricing(); }}
            className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors"
        >
            <HelpCircle size={14} />
        </button>

        {/* Stats Row */}
        <div className="flex justify-between items-end mb-2"> 
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
              {isPro ? 'Pro Credits' : 'Free Trial'}
            </p>
            <p className="text-lg font-bold text-white font-mono">
              {remaining} <span className="text-zinc-500 text-sm">/ {limit}</span>
            </p>
          </div>
          {isPro && (
            <Zap className={`w-5 h-5 ${remaining > 0 ? 'text-purple-400' : 'text-red-500'}`} />
          )}
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-zinc-900 rounded-full h-2 mb-3 overflow-hidden border border-zinc-800">
          <div className={`h-full rounded-full transition-all duration-500 ease-out ${barColor}`} style={{ width: `${percentage}%` }}></div>
        </div>

        {/* ACTION BUTTONS LOGIC */}
        {!isPro ? (
          <button onClick={onUpgrade} className="w-full py-1.5 text-xs font-bold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white rounded transition-all shadow-lg shadow-purple-500/20">
            Upgrade to Pro
          </button>
        ) : remaining <= 10 ? ( 
           // CHANGED: Now triggers when credits are 10 or less (was < 5)
           <button onClick={onRefill} className="w-full py-1.5 text-xs font-bold bg-red-500/10 border border-red-500/50 hover:bg-red-500/20 text-red-400 rounded transition-all animate-pulse flex items-center justify-center gap-2">
              <Zap size={12} /> Low Credits - Refill
           </button>
        ) : (
          <p className="text-[10px] text-zinc-500 text-center">Resets on billing date</p>
        )}
      </div>
    </div>
  );
};

// --- CINEMATIC CREATE PROJECT MODAL ---
const CreateProjectModal = ({ isOpen, onClose, onSave, isSaving, setNotification }) => {
    const [details, setDetails] = useState({
        title: "", logline: "", genre: "Action", budgetLevel: "Indie ($500k - $5M)",
        director: "", releaseWindow: "",
    });
    const [posterFile, setPosterFile] = useState(null);
    const [posterPreview, setPosterPreview] = useState(null);

    // Reset when opening
    useEffect(() => {
        if (isOpen) {
            setDetails({ title: "", logline: "", genre: "Action", budgetLevel: "Indie ($500k - $5M)", director: "", releaseWindow: "" });
            setPosterFile(null);
            setPosterPreview(null);
        }
    }, [isOpen]);

    const handleChange = (e) => setDetails({ ...details, [e.target.name]: e.target.value });

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setPosterFile(file);
            setPosterPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = () => {
        if (!details.title) {
            setNotification({ type: 'error', message: 'Project Title is required to greenlight.' });
            return;
        }
        onSave(details, posterFile);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Cinematic Backdrop with Blur */}
            <div 
                className="absolute inset-0 bg-black/80 backdrop-blur-xl transition-opacity duration-300" 
                onClick={onClose} 
            />

            {/* The "Greenlight" Card */}
            <div className="relative w-full max-w-4xl bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl shadow-purple-900/20 overflow-hidden flex flex-col md:flex-row animate-in fade-in zoom-in-95 duration-200">
                
                {/* DECORATIVE: Top Gradient Line */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600 opacity-50" />

                {/* LEFT COLUMN: The "Script" (Data Inputs) */}
                <div className="flex-1 p-8 md:p-10 space-y-6 overflow-y-auto max-h-[90vh]">
                    <div>
                        <h2 className="text-3xl font-bold text-white font-poppins tracking-tight mb-1">
                            Greenlight Project
                        </h2>
                        <p className="text-zinc-500 text-sm">Initialize a new workspace for your production.</p>
                    </div>

                    <div className="space-y-5">
                        {/* Title Input (Hero Input) */}
                        <div className="group">
                            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 group-focus-within:text-purple-400 transition-colors">
                                Working Title
                            </label>
                            <input
                                name="title"
                                value={details.title}
                                onChange={handleChange}
                                placeholder="Untitled Masterpiece"
                                className="w-full bg-transparent border-b-2 border-zinc-800 text-3xl font-bold text-white placeholder-zinc-700 py-2 focus:outline-none focus:border-purple-500 transition-colors font-poppins"
                                autoFocus
                            />
                        </div>

                        {/* Logline Input */}
                        <div className="group">
                            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2 group-focus-within:text-purple-400 transition-colors">
                                Logline / Premise
                            </label>
                            <textarea
                                name="logline"
                                value={details.logline}
                                onChange={handleChange}
                                rows={3}
                                placeholder="In a world where..."
                                className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-zinc-300 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all resize-none"
                            />
                        </div>

                        {/* Metadata Grid */}
                        <div className="grid grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Genre</label>
                                <div className="relative">
                                    <select
                                        name="genre"
                                        value={details.genre}
                                        onChange={handleChange}
                                        className="w-full appearance-none bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors cursor-pointer"
                                    >
                                        {["Action", "Horror", "Drama", "Comedy", "Sci-Fi", "Thriller", "Documentary", "Romance"].map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={16} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Budget Tier</label>
                                <div className="relative">
                                    <select
                                        name="budgetLevel"
                                        value={details.budgetLevel}
                                        onChange={handleChange}
                                        className="w-full appearance-none bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors cursor-pointer"
                                    >
                                        {["Micro (<$500k)", "Indie ($500k - $5M)", "Mid-Tier ($5M - $50M)", "Studio (>$50M)"].map(b => <option key={b} value={b}>{b}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={16} />
                                </div>
                            </div>
                        </div>

                        {/* Optional Info Toggle/Inputs */}
                        <div className="pt-2 grid grid-cols-2 gap-5">
                             <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Director</label>
                                <input 
                                    name="director" 
                                    value={details.director} 
                                    onChange={handleChange} 
                                    placeholder="Director Name"
                                    className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                                />
                             </div>
                             <div>
                                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-2">Release</label>
                                <input 
                                    name="releaseWindow" 
                                    value={details.releaseWindow} 
                                    onChange={handleChange} 
                                    placeholder="e.g. Fall 2025"
                                    className="w-full bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                                />
                             </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-8 flex items-center justify-between">
                        <button 
                            onClick={onClose}
                            className="text-zinc-500 hover:text-white transition-colors font-medium text-sm"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSubmit}
                            disabled={isSaving}
                            className="group relative px-8 py-3 bg-white text-black font-bold rounded-lg overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(255,255,255,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span className="relative z-10 flex items-center gap-2">
                                {isSaving ? <BlackboxLoaderIcon className="w-4 h-4" /> : <Zap size={18} className="text-purple-600 fill-current" />}
                                {isSaving ? "Initializing..." : "Initialize Project"}
                            </span>
                        </button>
                    </div>
                </div>

                {/* RIGHT COLUMN: The "Visuals" (Poster Upload) */}
                <div className="hidden md:flex w-80 bg-zinc-900/50 border-l border-zinc-800 flex-col items-center justify-center p-8 relative">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent pointer-events-none" />
                    
                    <div className="w-full aspect-[2/3] relative group cursor-pointer">
                        <input 
                            type="file" 
                            accept="image/*" 
                            onChange={handleFileSelect}
                            className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-pointer"
                        />
                        
                        <div className={`w-full h-full rounded-xl border-2 border-dashed transition-all duration-300 overflow-hidden relative flex flex-col items-center justify-center
                            ${posterPreview ? 'border-transparent bg-black' : 'border-zinc-700 bg-zinc-800/50 group-hover:border-purple-500 group-hover:bg-zinc-800'}`}
                        >
                            {posterPreview ? (
                                <>
                                    <img src={posterPreview} alt="Preview" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
                                        <p className="text-white font-medium text-sm flex items-center gap-2"><ImageIcon size={16}/> Change Art</p>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center p-4">
                                    <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform text-zinc-500 group-hover:text-purple-400">
                                        <UploadCloud size={24} />
                                    </div>
                                    <p className="text-zinc-300 font-bold text-sm mb-1">Upload Key Art</p>
                                    <p className="text-zinc-500 text-xs">Drop poster or concept art here</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <p className="text-zinc-600 text-xs mt-4 text-center">
                        This image will be used for your project dashboard and AI visual analysis.
                    </p>
                </div>
            </div>
        </div>
    );
};

// --- UI COMPONENTS ---

const SectionTitle = ({ icon: Icon, children, ...props }) => (
    <h2 className="text-3xl md:text-4xl text-zinc-900 dark:text-white tracking-tight mb-6 flex items-center gap-3 font-poppins-light-italic" {...props}>
        {Icon && <Icon className="w-8 h-8 text-purple-600 dark:text-purple-400" />}
        <span>{children}</span>
    </h2>
);

const AiContentModal = ({ isOpen, onClose, content, isLoading, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <AppCard className="p-8 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold font-poppins">{title}</h3>
          <AppButton onClick={onClose} variant="ghost" size="sm" icon={X}>
            Close
          </AppButton>
        </div>
        
        <div className="flex-grow overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <BlackboxLoaderIcon className="w-12 h-12" />
              <p className="ml-4 text-zinc-400">Generating content...</p>
            </div>
          ) : (
            <div className="prose prose-sm prose-invert max-w-none">
              <pre className="whitespace-pre-wrap text-zinc-300 bg-zinc-900 p-4 rounded-lg">
                {content}
              </pre>
            </div>
          )}
        </div>
        
        <div className="flex justify-end gap-3 pt-6 border-t border-zinc-800 mt-6">
          <AppButton onClick={() => navigator.clipboard.writeText(content)} variant="secondary" icon={Copy}>
            Copy to Clipboard
          </AppButton>
          <AppButton onClick={onClose}>
            Close
          </AppButton>
        </div>
      </AppCard>
    </div>
  );
};

const PLATFORMS = {
    instagram: { name: "Instagram", color: "bg-pink-500", brandIcon: () => <Instagram size={16} />, baseUrl: "https://www.instagram.com/" },
    tiktok: { name: "TikTok", color: "bg-cyan-400", brandIcon: () => <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"></path></svg>, baseUrl: "https://www.tiktok.com/@" },
    youtube: { name: "YouTube", color: "bg-red-600", brandIcon: () => <Youtube size={16} />, baseUrl: "https://www.youtube.com/" },
    facebook: { name: "Facebook", color: "bg-blue-600", brandIcon: () => <Facebook size={16} />, baseUrl: "https://www.facebook.com/" },
    twitter: { name: "X (Twitter)", color: "bg-gray-800", brandIcon: () => <Twitter size={16} />, baseUrl: "https://www.twitter.com/" },
};


const StatCard = ({ title, value, icon: Icon, color, className = '' }) => (
    <AppCard className={`p-4 ${className}`}>
      <div className="flex items-center">
        <div className={`p-3 rounded-lg mr-4 ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-zinc-400">{title}</p>
          <p className="text-2xl font-bold text-white font-inter">{value}</p>
        </div>
      </div>
    </AppCard>
);

const InfoCard = ({ icon: Icon, title, children, className }) => (
    <AppCard className={`p-6 ${className}`}>
        <h3 className="font-semibold text-lg text-zinc-900 dark:text-white mb-3 flex items-center gap-2 font-poppins">
            <Icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            {title}
        </h3>
        {children}
    </AppCard>
);

const AppInput = ({ ...props }) => <input {...props} className="w-full p-3 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors" />;
const AppTextarea = ({ ...props }) => <textarea {...props} className="w-full p-3 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors resize-y" />;
const AppSelect = ({ children, ...props }) => <select {...props} className="w-full p-3 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"> {children} </select>;

// New `ResetModal` component.
const ResetModal = ({ isOpen, onClose, onReset, isResetting }) => {
    const [selectedOptions, setSelectedOptions] = useState({});

    if (!isOpen) return null;

    // FIX: Update the keys to be associated with an array of fields to reset
    const options = [
        { label: 'Video Analysis & Keyframes', keys: ['video_analysis', 'video_keyframes_urls'] },
        { label: 'Visual Analysis & Posters', keys: ['visual_analysis', 'poster_a', 'poster_b'] },
        { label: 'Budget Planner Report', keys: ['budget_plan'] },
        { label: 'Revenue Forecast Report', keys: ['revenue_forecast'] },
        { label: 'Competitive Intel Report', keys: ['competitor_analysis'] },
        { label: 'Ad Concepts', keys: ['ad_concepts'] },
        { label: 'Script Intelligence Snapshots', keys: ['script_analyses'] },
        { label: 'Press Kit Data', keys: ['press_kit'] },
    ];
    
    // FIX: Update the toggle and confirm logic to handle a Map/Object for multiple keys
    const handleToggle = (option) => {
        setSelectedOptions(prev => {
            const newOptions = { ...prev };
            if (newOptions[option.label]) {
                delete newOptions[option.label];
            } else {
                newOptions[option.label] = option.keys;
            }
            return newOptions;
        });
    };

    
    const handleConfirm = () => {
        // Flatten the keys from the selected options and pass to onReset
        const allKeysToReset = Object.values(selectedOptions).flat();
        onReset(allKeysToReset);
        setSelectedOptions({});
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-lg w-full">
                <h3 className="text-xl font-bold font-poppins text-white mb-4">Reset Project Data</h3>
                <p className="text-zinc-400 mb-6">Select the data you want to clear from the current project. This action is permanent and cannot be undone.</p>
                <div className="space-y-4">
                    {options.map(option => (
                        <label key={option.label} className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors cursor-pointer ${selectedOptions[option.label] ? 'border-red-500 bg-red-500/10' : 'border-zinc-700 bg-zinc-800/50'}`}>
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={!!selectedOptions[option.label]}
                                    onChange={() => handleToggle(option)}
                                    className="w-5 h-5 text-red-600 bg-zinc-900 border-zinc-700 rounded focus:ring-red-500"
                                />
                                <span className="font-medium text-white">{option.label}</span>
                            </div>
                        </label>
                    ))}
                </div>
                <div className="flex justify-end gap-4 mt-8">
                    <AppButton onClick={onClose} variant="secondary" disabled={isResetting}>Cancel</AppButton>
                    <AppButton onClick={handleConfirm} variant="danger" disabled={isResetting || Object.keys(selectedOptions).length === 0}>
                        {isResetting ? 'Resetting...' : `Reset ${Object.keys(selectedOptions).length} Item${Object.keys(selectedOptions).length > 1 ? 's' : ''}`}
                    </AppButton>
                </div>
            </AppCard>
        </div>
    );
};

const ConfirmationModal = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirm" }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-md w-full">
                <h3 className="text-xl font-semibold text-zinc-900 dark:text-white mb-4 font-poppins">{title}</h3>
                <p className="text-zinc-600 dark:text-zinc-300 mb-6">{message}</p>
                <div className="flex justify-end gap-4">
                    <AppButton onClick={onCancel} variant="secondary">Cancel</AppButton>
                    <AppButton onClick={onConfirm} variant="danger">{confirmText}</AppButton>
                </div>
            </AppCard>
        </div>
    );
};


// Refactored CollapsibleSection component
const CollapsibleSection = ({ icon: Icon, title, children, isCollapsed, isOpen, onToggle }) => {
    const contentRef = useRef(null);

    return (
        <div className="overflow-hidden">
            <button
                onClick={onToggle}
                className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors text-sm font-bold ${isCollapsed ? 'justify-center' : ''} text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-800`}
            >
                <div className="flex items-center">
                    <Icon className="w-5 h-5" />
                    {!isCollapsed && <span className="ml-3">{title}</span>}
                </div>
                {!isCollapsed && (isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
            </button>
            <div
                ref={contentRef}
                style={{ maxHeight: !isCollapsed && isOpen ? `${contentRef.current?.scrollHeight}px` : '0px' }}
                className="transition-max-height duration-300 ease-in-out"
            >
                <div className={`pt-1 ${!isCollapsed ? 'pl-4' : ''} space-y-1`}>
                    {children}
                </div>
            </div>
        </div>
    );
};

// --- VIDEO PROCESSING UTILITY ---
const extractFramesFromVideo = (videoSource, numFrames = 15, setProgress) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.crossOrigin = "anonymous"; // Essential for loading cross-domain video files

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        const frames = [];

        video.onloadedmetadata = () => {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const duration = video.duration;
            if (!isFinite(duration) || duration === 0) {
                reject(new Error("Cannot determine video duration. The video may be invalid or inaccessible."));
                return;
            }
            const interval = duration / numFrames;
            let currentTime = 0;
            let framesExtracted = 0;

            const captureFrame = () => {
                if (framesExtracted >= numFrames) {
                    if (typeof videoSource !== 'string') URL.revokeObjectURL(video.src);
                    resolve(frames);
                    return;
                }
                video.currentTime = currentTime;
            };

            video.onseeked = () => {
                try {
                    context.drawImage(video, 0, 0, canvas.width, canvas.height);
                    frames.push(canvas.toDataURL('image/jpeg', 0.8));
                    framesExtracted++;
                    if (setProgress) setProgress(framesExtracted);
                    currentTime += interval;
                    if (currentTime > duration) currentTime = duration;
                    captureFrame();
                } catch(e) {
                     reject(new Error("Could not process video frame due to security (CORS) policy. Please try a different video source."));
                }
            };
            
            video.onerror = (e) => { 
                if (typeof videoSource !== 'string') URL.revokeObjectURL(video.src);
                reject(new Error("Error loading video. The URL may be invalid or blocked by CORS security policies.")); 
            };
            
            captureFrame();
        };

        if (typeof videoSource === 'string') {
            video.src = videoSource;
        } else {
            video.src = URL.createObjectURL(videoSource);
        }
        video.load();
    });
};


// --- TAB COMPONENTS ---

const PlaceholderComponent = ({ title, message }) => (
    <div className="p-8 bg-black/20 rounded-xl border border-zinc-800 h-full flex items-center justify-center">
        <div className="text-center">
            <h2 className="text-3xl font-bold text-white font-poppins">{title}</h2>
            <p className="text-zinc-400 mt-2">{message || "Functionality for this section will be built here."}</p>
        </div>
    </div>
);


// NEW: Image Regeneration Modal Component
const ImageRegenerationModal = ({ isOpen, onClose, originalPrompt, isRegenerating, onRegenerate }) => {
    const [editedPrompt, setEditedPrompt] = useState(originalPrompt);

    useEffect(() => {
        if (isOpen) {
            setEditedPrompt(originalPrompt);
        }
    }, [isOpen, originalPrompt]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        onRegenerate(editedPrompt);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-lg w-full">
                <h3 className="text-xl font-bold font-poppins text-white mb-4 flex items-center gap-2">
                    <WandSparkles className="text-purple-400" />
                    Refine & Regenerate Image
                </h3>
                <p className="text-zinc-400 mb-6">
                    Modify the AI's prompt to dial in your creative vision. Add specific details like character appearance, setting, or artistic style.
                </p>
                <div className="space-y-4">
                    <label htmlFor="prompt-textarea" className="font-semibold text-zinc-300">Image Prompt</label>
                    <AppTextarea
                        id="prompt-textarea"
                        value={editedPrompt}
                        onChange={(e) => setEditedPrompt(e.target.value)}
                        rows={5}
                        className="text-sm"
                    />
                </div>
                <div className="flex justify-end gap-4 mt-8">
                    <AppButton onClick={onClose} variant="secondary" disabled={isRegenerating}>Cancel</AppButton>
                    <AppButton onClick={handleConfirm} variant="primary" disabled={isRegenerating}>
                        {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                    </AppButton>
                </div>
            </AppCard>
        </div>
    );
};




/// --- NEW COMPONENT: Export Audience Data ---
const ExportAudienceData = ({ analysisData, filmTitle, setNotification }) => {
    if (!analysisData) return null;

    const handleDownloadJson = () => {
        try {
            const dataStr = JSON.stringify(analysisData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            const safeTitle = filmTitle?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'film';
            link.download = `${safeTitle}_audience_genius_data.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setNotification({ type: 'success', message: 'Audience data JSON downloaded.' });
        } catch (error) {
            console.error("Failed to download JSON:", error);
            setNotification({ type: 'error', message: 'Could not download the data file.' });
        }
    };

    return (
        <div className="bg-gradient-to-r from-blue-900/30 to-green-900/30 border border-blue-500/50 rounded-lg p-4 mt-6">
            <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-400" />
                Export Audience Data
            </h4>
            <p className="text-sm text-blue-200 mb-4">
                Download the complete Audience Genius analysis as a JSON file. This file can be uploaded to campaign management systems or used for further analysis.
            </p>
            <AppButton
                onClick={handleDownloadJson}
                icon={Download}
                variant="secondary"
            >
                Download Audience JSON
            </AppButton>
        </div>
    );
};

// --- PRESS & OUTREACH TAB ---

// ImageUploader Component - Reusable for VisualContentTab and PressKitBuilder
const ImageUploader = ({ onFileSelect, uploadedFile, label, setNotification }) => {
    const [isDragging, setIsDragging] = useState(false);
    const dropRef = useRef(null);
    const handleFileChange = (files) => {
        if (files && files[0] && files[0].type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => onFileSelect(e.target.result); // Pass base64 string
            reader.readAsDataURL(files[0]);
        } else {
            setNotification({ type: 'error', message: 'Please upload a valid image file.' });
        }
    };

    const handleDrag = (e) => { e.preventDefault(); e.stopPropagation(); };
    const handleDragIn = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragOut = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileChange(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    useEffect(() => {
        let div = dropRef.current;
        if (div) {
            div.addEventListener('dragenter', handleDragIn);
            div.addEventListener('dragleave', handleDragOut);
            div.addEventListener('dragover', handleDrag);
            div.addEventListener('drop', handleDrop);
        }
        return () => {
            if (div) {
                div.removeEventListener('dragenter', handleDragIn);
                div.removeEventListener('dragleave', handleDragOut);
                div.removeEventListener('dragover', handleDrag);
                div.removeEventListener('drop', handleDrop);
            }
        };
    }, []);

    return (
        <div className="w-full">
            <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-2 font-poppins">{label}</h3>
            <div ref={dropRef} className={`relative w-full h-80 border-2 border-dashed ${isDragging ? 'border-purple-500 bg-purple-500/10' : 'border-zinc-300 dark:border-zinc-700'} rounded-2xl flex justify-center items-center text-center p-4 transition-all`}>
                {uploadedFile ? (
                    <img src={uploadedFile} alt="Uploaded poster" className="w-full h-full object-contain rounded-lg"/>
                ) : (
                    <div className="text-zinc-500 dark:text-zinc-400">
                        <UploadCloud className="w-12 h-12 mx-auto mb-2" />
                        <p>Drag & drop or click to upload</p>
                    </div>
                )}
                <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => handleFileChange(e.target.files)} accept="image/*" />
            </div>
        </div>
    );
};

// InfluencerTool Component - Properly structured
const InfluencerTool = ({ filmData, setNotification, influencerList, addInfluencer }) => {
  const [discoveredInfluencers, setDiscoveredInfluencers] = useState([]);
  const [loadingInfluencers, setLoadingInfluencers] = useState(false);

  const latestAudienceAnalysis = useMemo(() => {
    if (!filmData.ai_analyses || filmData.ai_analyses.length === 0) return null;
    return filmData.ai_analyses[filmData.ai_analyses.length - 1].analysis;
  }, [filmData.ai_analyses]);

  const handleDiscoverInfluencers = async () => {
    if (!latestAudienceAnalysis) {
      setNotification({ type: 'error', message: 'Run Audience Analysis first for targeted influencer discovery.' });
      return;
    }
    setLoadingInfluencers(true);
    setDiscoveredInfluencers([]);
    try {
      const result = await generateInfluencerListLLM(filmData, latestAudienceAnalysis);
      setDiscoveredInfluencers(result);
      setNotification({ type: 'success', message: 'mi has discovered potential influencers!' });
    } catch (error) {
      setNotification({ type: 'error', message: `Failed to discover influencers: ${error.message}` });
    } finally {
      setLoadingInfluencers(false);
    }
  };

  const handleAddInfluencerToList = async (influencerData) => {
    await addInfluencer(influencerData);
  };

  return (
    <InfoCard icon={Handshake} title="AI Influencer Discovery">
      <AppButton 
        onClick={handleDiscoverInfluencers}
        disabled={loadingInfluencers || !latestAudienceAnalysis}
        className="w-full mb-6" 
        icon={BrainCircuit}
      >
        {loadingInfluencers ? 'Discovering...' : 'Discover Influencers'}
      </AppButton>
      {!latestAudienceAnalysis && (
        <p className="text-yellow-400 text-xs mt-2 text-center">
          Note: Run Audience Genius analysis first to enable this feature.
        </p>
      )}
      {loadingInfluencers && (
        <div className="flex justify-center py-10">
          <BlackboxLoaderIcon className="w-10 h-10" />
        </div>
      )}
      {discoveredInfluencers.length > 0 && (
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
          {discoveredInfluencers.map((inf, i) => {
            const isAdded = influencerList.some(item => item.handle === inf.handle && item.platform === inf.platform);
            const PlatformIcon = PLATFORMS[inf.platform.toLowerCase()]?.brandIcon || (() => null);
            return (
              <div key={i} className="bg-zinc-800/50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`p-1 rounded-full ${PLATFORMS[inf.platform.toLowerCase()].color}`}>
                      <PlatformIcon />
                    </div>
                    <div>
                      <p className="font-bold text-white">{inf.name}</p>
                      <p className="text-sm text-zinc-400">@{inf.handle} - {inf.followers} Followers</p>
                    </div>
                  </div>
                  <AppButton 
                    onClick={() => handleAddInfluencerToList(inf)}
                    variant={isAdded ? "secondary" : "primary"} 
                    size="sm"
                    icon={isAdded ? CheckCircle : Plus} 
                    disabled={isAdded}
                  >
                    {isAdded ? "Added" : "Add to List"}
                  </AppButton>
                </div>
                <div className="text-xs text-zinc-300 bg-zinc-900 p-2 rounded">
                  <p><strong className="text-purple-400">Reasoning:</strong> {inf.reasoning}</p>
                  <p className="mt-1"><strong className="text-purple-400">Outreach Idea:</strong> {inf.outreach_idea}</p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </InfoCard>
  );
};

// Smart Template System - Add this as a separate component
const SmartTemplateSelector = ({ filmData, pressKitData, setPressKitData, setSelectedSections, setNotification }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [isApplying, setIsApplying] = useState(false);

  const PRESS_KIT_TEMPLATES = {
    horror: {
      name: "Horror/Thriller",
      description: "Perfect for horror, thriller, and suspense films",
      icon: "🎭",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: true,
        castCrew: true,
        scareFactors: true,
        practicalEffects: true,
        credits: true,
        photos: false
      },
      suggestedContent: {
        synopsis: "Focus on the central terror/threat without spoiling key scares. Emphasize atmosphere and psychological elements.",
        director_statement: "Discuss your vision for creating fear, influences from classic horror, and unique approach to the genre.",
        additionalSections: ["Scare Factors", "Practical Effects & Makeup", "Sound Design"]
      },
      colorScheme: "bg-red-900/20 border-red-500/50 text-red-300"
    },
    drama: {
      name: "Drama/Indie",
      description: "Ideal for character-driven dramas and indie films",
      icon: "🎬",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: true,
        castCrew: true,
        socialImpact: true,
        awards: true,
        credits: true,
        photos: true
      },
      suggestedContent: {
        synopsis: "Emphasize character arcs, emotional journey, and real-world relevance. Highlight any social themes.",
        director_statement: "Share your personal connection to the story and why this story needs to be told now.",
        additionalSections: ["Social Impact", "Awards & Recognition", "Behind the Scenes"]
      },
      colorScheme: "bg-blue-900/20 border-blue-500/50 text-blue-300"
    },
    action: {
      name: "Action/Adventure", 
      description: "High-octane action films and blockbusters",
      icon: "💥",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: false,
        castCrew: true,
        stunts: true,
        locations: true,
        credits: true,
        photos: true
      },
      suggestedContent: {
        synopsis: "Lead with the stakes and action. Emphasize the thrill, adventure, and spectacular sequences.",
        director_statement: "Focus on action choreography, practical effects, and creating authentic thrills.",
        additionalSections: ["Stunt Sequences", "Filming Locations", "Production Scale"]
      },
      colorScheme: "bg-orange-900/20 border-orange-500/50 text-orange-300"
    },
    comedy: {
      name: "Comedy",
      description: "Comedies, rom-coms, and feel-good films",
      icon: "😂",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: true,
        castCrew: true,
        comedy: true,
        chemistry: true,
        credits: true,
        photos: false
      },
      suggestedContent: {
        synopsis: "Capture the humor and heart. Tease the comedy without spoiling the best jokes.",
        director_statement: "Discuss your comedic influences and approach to balancing humor with heart.",
        additionalSections: ["Comedy Style", "Cast Chemistry", "Improvisation"]
      },
      colorScheme: "bg-yellow-900/20 border-yellow-500/50 text-yellow-300"
    },
    documentary: {
      name: "Documentary",
      description: "Non-fiction films and documentaries", 
      icon: "📹",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: true,
        castCrew: false,
        subject: true,
        research: true,
        impact: true,
        credits: true,
        photos: true
      },
      suggestedContent: {
        synopsis: "Present the compelling real story and why audiences need to know about this subject.",
        director_statement: "Explain what drew you to this subject and your approach to telling this true story.",
        additionalSections: ["Subject Background", "Research Process", "Real-World Impact"]
      },
      colorScheme: "bg-green-900/20 border-green-500/50 text-green-300"
    },
    festival: {
      name: "Festival Premiere",
      description: "Optimized for film festival submissions",
      icon: "🏆",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: true,
        castCrew: true,
        awards: true,
        filmography: true,
        credits: true,
        photos: true
      },
      suggestedContent: {
        synopsis: "Artistic and sophisticated tone. Emphasize unique voice and festival-worthy elements.",
        director_statement: "Highlight your artistic vision, influences, and what makes this film distinctive.",
        additionalSections: ["Previous Awards", "Director Filmography", "Festival Strategy"]
      },
      colorScheme: "bg-purple-900/20 border-purple-500/50 text-purple-300"
    },
    custom: {
      name: "Custom Build",
      description: "Start from scratch with full customization",
      icon: "🎨",
      sections: {
        poster: true,
        factSheet: true,
        synopsis: true,
        directorStatement: false,
        castCrew: false,
        credits: false,
        photos: false
      },
      colorScheme: "bg-zinc-800/50 border-zinc-600 text-zinc-300"
    }
  };

  const detectBestTemplate = () => {
    const genre = filmData.genre?.toLowerCase() || '';
    const themes = filmData.themes?.toLowerCase() || '';
    const logline = filmData.logline?.toLowerCase() || '';
    
    const content = `${genre} ${themes} ${logline}`;
    
    if (content.includes('horror') || content.includes('thriller') || content.includes('scary') || content.includes('fear')) {
      return 'horror';
    }
    if (content.includes('action') || content.includes('adventure') || content.includes('fight') || content.includes('chase')) {
      return 'action';
    }
    if (content.includes('comedy') || content.includes('funny') || content.includes('humor') || content.includes('laugh')) {
      return 'comedy';
    }
    if (content.includes('documentary') || content.includes('real story') || content.includes('true events')) {
      return 'documentary';
    }
    if (content.includes('drama') || content.includes('character') || content.includes('emotional')) {
      return 'drama';
    }
    if (filmData.themes?.includes('festival') || filmData.releaseWindow?.includes('festival')) {
      return 'festival';
    }
    
    return 'custom';
  };

  const applyTemplate = async (templateKey) => {
    if (templateKey === 'custom') return;
    
    setIsApplying(true);
    const template = PRESS_KIT_TEMPLATES[templateKey];
    
    try {
      // Apply section selections
      setSelectedSections(template.sections);
      
      // Auto-populate content based on existing film data
      const autoContent = {
        logline: pressKitData.logline || filmData.logline || '',
        synopsis: pressKitData.synopsis || template.suggestedContent.synopsis,
        director_statement: pressKitData.director_statement || template.suggestedContent.director_statement,
        runtime: pressKitData.runtime || '',
        musicCredits: pressKitData.musicCredits || '',
        posterUrl: pressKitData.posterUrl || filmData.poster_a || '',
        cast: pressKitData.cast || [],
        crew: pressKitData.crew || [],
        full_credits: pressKitData.full_credits || []
      };

      setPressKitData(prev => ({ ...prev, ...autoContent }));
      
      setNotification({ 
        type: 'success', 
        message: `${template.name} template applied! Press kit optimized for ${template.description.toLowerCase()}.` 
      });
      
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to apply template.' });
    } finally {
      setIsApplying(false);
    }
  };

  const recommendedTemplate = detectBestTemplate();

  return (
    <div className="mb-8 space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-white font-poppins mb-2">Smart Template System</h3>
        <p className="text-zinc-400">Choose a template optimized for your film's genre and target audience</p>
      </div>

      {/* AI Recommendation */}
      {recommendedTemplate !== 'custom' && (
        <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
              <WandSparkles className="w-4 h-4 text-white" />
            </div>
            <h4 className="font-bold text-purple-300">mi Recommendation</h4>
          </div>
          <p className="text-sm text-purple-200 mb-3">
            Based on your film data, we recommend the <strong>{PRESS_KIT_TEMPLATES[recommendedTemplate].name}</strong> template.
          </p>
          <AppButton 
            onClick={() => {
              setSelectedTemplate(recommendedTemplate);
              applyTemplate(recommendedTemplate);
            }}
            size="sm" 
            icon={WandSparkles}
            className="bg-purple-600 hover:bg-purple-500"
          >
            Apply Recommended Template
          </AppButton>
        </div>
      )}

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(PRESS_KIT_TEMPLATES).map(([key, template]) => (
          <button
            key={key}
            onClick={() => {
              setSelectedTemplate(key);
              applyTemplate(key);
            }}
            disabled={isApplying}
            className={`p-4 rounded-lg border-2 transition-all text-left relative ${
              selectedTemplate === key
                ? template.colorScheme
                : 'border-zinc-600 bg-zinc-800/50 text-zinc-400 hover:border-zinc-500'
            } ${isApplying ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}`}
          >
            {/* Recommendation Badge */}
            {key === recommendedTemplate && key !== 'custom' && (
              <div className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                RECOMMENDED
              </div>
            )}
            
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">{template.icon}</span>
              <div>
                <h4 className="font-bold text-lg">{template.name}</h4>
                {template.additionalSections && (
                  <p className="text-xs opacity-75">
                    +{template.additionalSections.length} specialized sections
                  </p>
                )}
              </div>
            </div>
            
            <p className="text-sm opacity-90 mb-3">{template.description}</p>
            
            {template.additionalSections && (
              <div className="text-xs space-y-1">
                <p className="font-semibold opacity-75">Includes:</p>
                <div className="flex flex-wrap gap-1">
                  {template.additionalSections.slice(0, 2).map(section => (
                    <span key={section} className="bg-black/20 px-2 py-1 rounded text-xs">
                      {section}
                    </span>
                  ))}
                  {template.additionalSections.length > 2 && (
                    <span className="text-xs opacity-60">+{template.additionalSections.length - 2} more</span>
                  )}
                </div>
              </div>
            )}
            
            {selectedTemplate === key && (
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20 rounded-lg flex items-center justify-center">
                <div className="bg-black/60 text-white px-3 py-1 rounded-full text-sm font-bold">
                  ACTIVE
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Template Preview */}
      {selectedTemplate !== 'custom' && PRESS_KIT_TEMPLATES[selectedTemplate] && (
        <div className={`p-4 rounded-lg ${PRESS_KIT_TEMPLATES[selectedTemplate].colorScheme}`}>
          <h4 className="font-bold mb-2">Template Features:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold opacity-75">Optimized Sections:</p>
              <p>{Object.entries(PRESS_KIT_TEMPLATES[selectedTemplate].sections).filter(([_, enabled]) => enabled).length} sections included</p>
            </div>
            <div>
              <p className="font-semibold opacity-75">Content Focus:</p>
              <p>{PRESS_KIT_TEMPLATES[selectedTemplate].description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Complete Enhanced Press Kit Generator - Replace your existing one with this entire component

/* Replace the existing KitBuilder component with this code */
const KitBuilder = ({
  filmData, pressKitData, setNotification,
  handlePressKitChange, handleCreditChange, handleAddCredit,
  handleDeleteCredit, handleToggleFeature, handleGenerateBio,
  handleAiGenerate, isAiLoading, csvInputRef, handleCsvImport,
  handleFeaturedMemberChange
}) => {
  const [openSections, setOpenSections] = useState({
    poster: true, factSheet: true, logline: true, director: true,
    credits: true, featured: true, ai: false,
  });

  const toggleSection = (section) => setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
      {/* --- INPUT FORMS (LEFT SIDE) --- */}
      <div className="space-y-4 overflow-y-auto pr-4 pb-16">
        {/* Poster Section */}
        <button onClick={() => toggleSection('poster')} className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800">
          Poster Image {openSections.poster ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.poster && (
          <AppCard className="p-4">
            <ImageUploader
              label="Official Poster"
              uploadedFile={pressKitData.posterUrl}
              onFileSelect={(base64) => handlePressKitChange('posterUrl', base64)}
              setNotification={setNotification}
            />
          </AppCard>
        )}

        {/* Fact Sheet Section */}
        <button onClick={() => toggleSection('factSheet')} className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800">
          Fact Sheet Details {openSections.factSheet ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.factSheet && (
          <AppCard className="p-4 space-y-4">
            <div>
              <label className="font-semibold block mb-2">Runtime (e.g., "97 minutes")</label>
              <AppInput type="text" value={pressKitData.runtime || ''} onChange={e => handlePressKitChange('runtime', e.target.value)} />
            </div>
            <div>
              <label className="font-semibold block mb-2">Music & Licensing Credits</label>
              <AppTextarea rows="4" value={pressKitData.musicCredits || ''} onChange={e => handlePressKitChange('musicCredits', e.target.value)} placeholder="List key music credits, one per line." />
            </div>
          </AppCard>
        )}

        {/* Logline & Synopsis Section */}
        <button onClick={() => toggleSection('logline')} className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800">
          Logline & Synopsis {openSections.logline ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.logline && (
          <div className="space-y-4 p-2">
            <AppCard className="p-4">
              <label className="font-semibold block mb-2">Logline</label>
              <AppTextarea rows="2" value={pressKitData.logline || ''} onChange={e => handlePressKitChange('logline', e.target.value)} />
            </AppCard>
            <AppCard className="p-4">
              <label className="font-semibold block mb-2">Synopsis</label>
              <AppTextarea rows="5" value={pressKitData.synopsis || ''} onChange={e => handlePressKitChange('synopsis', e.target.value)} />
            </AppCard>
          </div>
        )}

        {/* Director's Statement Section */}
        <button onClick={() => toggleSection('director')} className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800">
          Director's Statement {openSections.director ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.director && (
          <div className="p-2">
            <AppCard className="p-4">
              <AppTextarea rows="5" value={pressKitData.director_statement || ''} onChange={e => handlePressKitChange('director_statement', e.target.value)} />
            </AppCard>
          </div>
        )}

        {/* Featured Bios Section */}
        <button onClick={() => toggleSection('featured')} className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800">
          Featured Cast & Crew Bios {openSections.featured ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.featured && (
          <div className="p-2 space-y-4">
            {(pressKitData.cast || []).concat(pressKitData.crew || []).map(member => (
              <AppCard key={member.id} className="p-4">
                <div className="flex gap-4 items-start">
                  <div className="w-24 flex-shrink-0">
                      <ImageUploader
                          label=""
                          uploadedFile={member.headshotUrl}
                          onFileSelect={(base64) => handleFeaturedMemberChange(member.id, 'headshotUrl', base64)}
                          setNotification={setNotification}
                      />
                  </div>
                  <div className="flex-grow space-y-2">
                    <input className="w-full p-2 bg-zinc-800 rounded-md font-bold" value={member.name} readOnly />
                    <input className="w-full p-2 bg-zinc-800 rounded-md text-sm" value={member.role} readOnly />
                  </div>
                </div>
                <div className="relative mt-2">
                  <label className="font-semibold text-sm mb-1 block">Bio</label>
                  <AppTextarea
                    value={member.bio || ''}
                    onChange={e => handleFeaturedMemberChange(member.id, 'bio', e.target.value)}
                    rows="4"
                    className="pr-10"
                  />
                  <AppButton
                    onClick={() => handleGenerateBio(member)}
                    size="sm"
                    variant="ghost"
                    className="!p-1 absolute top-8 right-2"
                    icon={WandSparkles}
                    title="Generate Bio with mi"
                  />
                </div>
              </AppCard>
            ))}
            {(pressKitData.cast?.length === 0 && pressKitData.crew?.length === 0) && (
                <p className="text-zinc-400 text-center text-sm p-4">No featured members. Go to the "Full Credit List" to add and feature cast/crew.</p>
            )}
          </div>
        )}

        {/* --- CORRECTED SECTION: Full Credit List --- */}
        <button
          onClick={() => toggleSection('credits')}
          className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800"
        >
          Full Credit List {openSections.credits ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.credits && (
          <AppCard className="p-4 space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-zinc-400 text-sm">Add each cast/crew. Use the toggle to feature them with a bio.</p>
              <div className="flex gap-2">
                <AppButton onClick={() => csvInputRef.current?.click()} size="sm" variant="secondary">Import CSV</AppButton>
                <input type="file" ref={csvInputRef} onChange={handleCsvImport} accept=".csv" className="hidden" />
                <AppButton onClick={handleAddCredit} size="sm" icon={Plus}>Add Credit</AppButton>
              </div>
            </div>
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {(pressKitData.full_credits || []).map((credit, index) => (
                <div key={credit.id || index} className="bg-zinc-800/50 p-4 rounded-lg border border-zinc-700">
                  {/* This is the corrected grid layout for each credit row */}
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Column 1: Feature Toggle */}
                    <div className="col-span-1 flex flex-col items-center">
                      <label className="text-xs text-zinc-400 mb-2 text-center">Feature</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!credit.is_featured}
                          onChange={() => handleToggleFeature(index)}
                          className="sr-only"
                        />
                        <div className={`relative w-10 h-5 rounded-full transition-colors ${
                          credit.is_featured ? 'bg-purple-600' : 'bg-zinc-600'
                        }`}>
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                            credit.is_featured ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </div>
                      </label>
                    </div>

                    {/* Column 2: Role Input */}
                    <div className="col-span-4">
                      <label className="text-xs text-zinc-400 mb-1 block">Role</label>
                      <AppInput
                        placeholder="Role"
                        value={credit.role || ''}
                        onChange={(e) => handleCreditChange(index, 'role', e.target.value)}
                      />
                    </div>
                    {/* Column 3: Name Input */}
                    <div className="col-span-4">
                      <label className="text-xs text-zinc-400 mb-1 block">Name</label>
                      <AppInput
                        placeholder="Name"
                        value={credit.name || ''}
                        onChange={(e) => handleCreditChange(index, 'name', e.target.value)}
                      />
                    </div>
                    {/* Column 4: Department Dropdown */}
                    <div className="col-span-2">
                      <label className="text-xs text-zinc-400 mb-1 block">Department</label>
                      <AppSelect
                        value={credit.department || 'Crew'}
                        onChange={(e) => handleCreditChange(index, 'department', e.target.value)}
                      >
                        <option value="Crew">Crew</option>
                        <option value="Cast">Cast</option>
                      </AppSelect>
                    </div>
                    {/* Column 5: Delete Button */}
                    <div className="col-span-1 flex items-end justify-center h-full pb-2">
                      <AppButton
                        variant="danger"
                        size="sm"
                        className="!p-2"
                        onClick={() => handleDeleteCredit(index)}
                        icon={Trash2}
                        title="Delete Credit"
                      />
                    </div>
                  </div>

                  {/* Feature status indicator */}
                  {credit.is_featured && (
                    <div className="mt-3 pt-3 border-t border-zinc-600">
                      <div className="flex items-center gap-2 text-sm text-green-400">
                        <Star className="w-4 h-4" />
                        <span>This person will appear in the featured cast/crew section with bio</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Empty state */}
              {(!pressKitData.full_credits || pressKitData.full_credits.length === 0) && (
                <div className="text-center py-8 text-zinc-400">
                  <Users className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                  <p className="font-semibold">No credits added yet</p>
                  <p className="text-sm">Click "Add Credit" to start building your cast and crew list</p>
                </div>
              )}
            </div>
          </AppCard>
        )}

        {/* AI Workflow Tools Section */}
        <button onClick={() => toggleSection('ai')} className="w-full text-left font-semibold text-xl flex justify-between items-center p-2 rounded-md hover:bg-zinc-800">
          mi Workflow Tools {openSections.ai ? <ChevronUp /> : <ChevronDown />}
        </button>
        {openSections.ai && (
          <AppCard className="p-4 flex gap-4">
            <AppButton onClick={() => handleAiGenerate('Press Release')} icon={Newspaper} disabled={isAiLoading}>Draft Press Release</AppButton>
            <AppButton onClick={() => handleAiGenerate('Social Media Pack')} icon={Megaphone} disabled={isAiLoading}>Generate Social Pack</AppButton>
          </AppCard>
        )}
      </div>

      {/* --- LIVE PREVIEW PANEL (RIGHT SIDE) --- */}
      <div className="bg-white text-black rounded-lg shadow-2xl overflow-y-auto p-12 font-[serif]">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold uppercase tracking-widest">{filmData.title || "Film Title"}</h1>
          <p className="text-gray-600 mt-2">PRODUCTION NOTES</p>
        </div>

        {pressKitData.posterUrl && (
            <div className="mb-12">
                <img src={pressKitData.posterUrl} alt="Official Poster" className="w-full object-contain max-h-[70vh] mx-auto" />
            </div>
        )}

        <div className="space-y-12">
            {pressKitData.logline && <div>
                <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">LOGLINE</h2>
                <p className="text-gray-700">{pressKitData.logline}</p>
            </div>}

            {pressKitData.synopsis && <div>
                <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">SYNOPSIS</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{pressKitData.synopsis}</p>
            </div>}

            {pressKitData.director_statement && <div>
                <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">DIRECTOR'S STATEMENT</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{pressKitData.director_statement}</p>
            </div>}

          {(pressKitData.cast?.length > 0 || pressKitData.crew?.length > 0) && (
            <div>
              <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">ABOUT THE CAST & CREW</h2>
              {[...(pressKitData.cast || []), ...(pressKitData.crew || [])].map(member => (
                <div key={member.id} className="mb-6">
                    <h3 className="text-2xl font-bold">{member.name}</h3>
                    <p className="text-lg text-gray-500 italic mb-2">{member.role}</p>
                    {member.headshotUrl && <img src={member.headshotUrl} alt={member.name} className="w-32 h-32 object-cover float-left mr-4 mb-2 rounded-md" />}
                    <p className="text-gray-700 whitespace-pre-wrap">{member.bio}</p>
                    <div style={{clear: 'both'}}></div>
                </div>
              ))}
            </div>
          )}

            <div>
              <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">TECHNICAL SPECS</h2>
              <p className="text-gray-700"><strong>Runtime:</strong> {pressKitData.runtime || 'TBD'}</p>
            </div>

            {pressKitData.musicCredits && <div>
                <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">MUSIC CREDITS</h2>
                <p className="text-gray-700 whitespace-pre-wrap">{pressKitData.musicCredits}</p>
            </div>}

          {(pressKitData.full_credits?.length > 0) && (
            <div>
              <h2 className="text-lg font-bold border-b-2 border-black pb-1 mb-2 tracking-widest">FULL CREDITS</h2>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 mt-4 text-sm break-words">
                {pressKitData.full_credits?.map((credit, i) => <div key={credit.id || i}><strong>{credit.role}</strong><br />{credit.name}</div>)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EnhancedPressKitGenerator = ({ filmData, pressKitData, setNotification }) => {
  const [selectedOrientation, setSelectedOrientation] = useState('landscape');
  const [isExporting, setIsExporting] = useState(false);
  
  // State for section inclusion, mirroring the old logic
  const [localSelectedSections, setLocalSelectedSections] = useState({
    poster: true, factSheet: true, synopsis: true, directorStatement: true,
    castCrew: true, credits: true, photos: false
  });

  const orientationOptions = [
    {
      id: 'landscape',
      label: 'Landscape (11" × 8.5")',
      description: 'Ideal for digital viewing and presentations',
      icon: '📱',
      dimensions: '279mm × 216mm'
    },
    {
      id: 'portrait',
      label: 'Portrait (8.5" × 11")',
      description: 'Standard print format for traditional press kits',
      icon: '📄',
      dimensions: '216mm × 279mm'
    }
  ];

  const sectionOptions = [
    { id: 'poster', label: 'Movie Poster', icon: ImageIcon, required: true },
    { id: 'factSheet', label: 'Fact Sheet', icon: FileText, required: true },
    { id: 'synopsis', label: 'Synopsis', icon: FileText, required: true },
    { id: 'directorStatement', label: "Director's Statement", icon: Users, required: false },
    { id: 'castCrew', label: 'Cast & Crew Profiles', icon: Users, required: false },
    { id: 'credits', label: 'Full Credits', icon: FileText, required: false },
    { id: 'photos', label: 'Production Photos', icon: ImageIcon, required: false }
  ];
  

  const handleSectionToggle = (sectionId) => {
    const section = sectionOptions.find(s => s.id === sectionId);
    if (section?.required) return;
    setLocalSelectedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const generateProfessionalPressKit = async () => {
    if (!filmData || !filmData.title) {
      setNotification({ type: 'error', message: "No project data to export." });
      return;
    }

    setIsExporting(true);
    setNotification({ type: 'info', message: 'Generating professional press kit...' });
    
    try {
      const { jsPDF } = window.jspdf;
      const isLandscape = selectedOrientation === 'landscape';
      const doc = new jsPDF(isLandscape ? 'l' : 'p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = 30;

      const addPageBreak = () => {
        doc.addPage();
        yPosition = margin;
      };

      const addSection = (title, content, fontSize = 12, startY = yPosition) => {
        if (startY + 40 > pageHeight - margin) {
          addPageBreak();
          startY = yPosition;
        }

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(title.toUpperCase(), margin, startY);
        
        doc.setDrawColor(0);
        doc.line(margin, startY + 2, pageWidth - margin, startY + 2);
        yPosition = startY + 12;

        if (content) {
          doc.setFontSize(fontSize);
          doc.setFont('helvetica', 'normal');
          const splitText = doc.splitTextToSize(content, pageWidth - (margin * 2));
          doc.text(splitText, margin, yPosition);
          yPosition += doc.getTextDimensions(splitText).h + 10;
        }
      };

      const addImage = async (imageUrl, maxWidth = 100, maxHeight = 140) => {
        if (!imageUrl) return;
        
        try {
          const img = new window.Image();
          img.crossOrigin = "Anonymous";
          
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = imageUrl;
          });

          let width = maxWidth;
          let height = (img.height * width) / img.width;
          
          if (height > maxHeight) {
            height = maxHeight;
            width = (img.width * height) / img.height;
          }

          if (yPosition + height > pageHeight - margin) {
            addPageBreak();
          }

          doc.addImage(img, 'PNG', (pageWidth - width) / 2, yPosition, width, height);
          yPosition += height + 15;
        } catch (e) {
          console.error("Could not add image:", e);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'italic');
          doc.text('[Image could not be loaded]', margin, yPosition);
          yPosition += 10;
        }
      };

      // TITLE PAGE
      doc.setFontSize(42);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(filmData.title.toUpperCase(), pageWidth - 40);
      doc.text(titleLines, pageWidth / 2, 60, { align: 'center' });

      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text("PRODUCTION NOTES", pageWidth / 2, 85, { align: 'center' });

      yPosition = 110;

      // POSTER (if selected and available)
      if (localSelectedSections.poster && pressKitData.posterUrl) {
        await addImage(pressKitData.posterUrl, isLandscape ? 120 : 100, isLandscape ? 160 : 140);
      }

      // FACT SHEET (if selected)
      if (localSelectedSections.factSheet) {
        addPageBreak();
        addSection("FACT SHEET", null);
        
        const factSheetData = [
          ["Title:", filmData.title || ""],
          ["Genre:", filmData.genre || ""],
          ["Runtime:", pressKitData.runtime || "TBD"],
          ["Director:", filmData.director || ""],
          ["Stars:", filmData.stars || ""],
          ["Release Window:", filmData.releaseWindow || "TBD"]
        ];

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        
        factSheetData.forEach(([label, value]) => {
          if (value) {
            doc.setFont('helvetica', 'bold');
            doc.text(label, margin, yPosition);
            doc.setFont('helvetica', 'normal');
            doc.text(value, margin + 40, yPosition);
            yPosition += 8;
          }
        });
        yPosition += 10;
      }

      // SYNOPSIS (if selected)
      if (localSelectedSections.synopsis && (pressKitData.logline || pressKitData.synopsis)) {
        if (pressKitData.logline) {
          addSection("LOGLINE", pressKitData.logline);
        }
        if (pressKitData.synopsis) {
          addSection("SYNOPSIS", pressKitData.synopsis);
        }
      }

      // DIRECTOR'S STATEMENT (if selected)
      if (localSelectedSections.directorStatement && pressKitData.director_statement) {
        addSection("DIRECTOR'S STATEMENT", pressKitData.director_statement);
      }

      // CAST & CREW PROFILES (if selected)
      if (localSelectedSections.castCrew) {
        const featuredMembers = [...(pressKitData.cast || []), ...(pressKitData.crew || [])];
        
        if (featuredMembers.length > 0) {
          addPageBreak();
          addSection("ABOUT THE CAST & CREW", null);

          for (const member of featuredMembers) {
            if (yPosition + 80 > pageHeight - margin) {
              addPageBreak();
            }

            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text(member.name, margin, yPosition);
            yPosition += 8;

            doc.setFontSize(12);
            doc.setFont('helvetica', 'italic');
            doc.text(member.role, margin, yPosition);
            yPosition += 12;

            let textStartX = margin;
            if (member.headshotUrl) {
              try {
                const img = new window.Image();
                img.crossOrigin = "Anonymous";
                await new Promise((resolve, reject) => {
                  img.onload = resolve;
                  img.onerror = reject;
                  img.src = member.headshotUrl;
                });

                const imgSize = 35;
                doc.addImage(img, 'PNG', margin, yPosition, imgSize, imgSize);
                textStartX = margin + imgSize + 8;
              } catch (e) {
                console.error("Could not add headshot for", member.name);
              }
            }

            if (member.bio) {
              doc.setFontSize(11);
              doc.setFont('helvetica', 'normal');
              const bioText = doc.splitTextToSize(member.bio, pageWidth - textStartX - margin);
              doc.text(bioText, textStartX, yPosition);
              yPosition += Math.max(35, doc.getTextDimensions(bioText).h);
            } else {
              yPosition += 35;
            }
            
            yPosition += 15;
          }
        }
      }

      // FULL CREDITS (if selected)
      if (localSelectedSections.credits && pressKitData.full_credits?.length > 0) {
        addPageBreak();
        addSection("FULL CREDITS", null);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        const credits = pressKitData.full_credits;
        const columnsPerPage = isLandscape ? 3 : 2;
        const columnWidth = (pageWidth - (margin * 2)) / columnsPerPage;
        let currentColumn = 0;
        let columnY = yPosition;

        credits.forEach((credit, index) => {
          const creditHeight = 16;

          if (columnY + creditHeight > pageHeight - margin) {
            if (currentColumn < columnsPerPage - 1) {
              currentColumn++;
              columnY = yPosition;
            } else {
              addPageBreak();
              currentColumn = 0;
              columnY = yPosition;
            }
          }

          const xPos = margin + (currentColumn * columnWidth);
          
          doc.setFont('helvetica', 'bold');
          doc.text(credit.role, xPos, columnY);
          doc.setFont('helvetica', 'normal');
          doc.text(credit.name, xPos, columnY + 6);
          
          columnY += creditHeight;
        });
      }

      // TECHNICAL SPECS
      addPageBreak();
      addSection("TECHNICAL SPECIFICATIONS", null);
      
      const techSpecs = [
        ["Runtime:", pressKitData.runtime || "TBD"],
        ["Format:", "Digital"],
        ["Aspect Ratio:", "TBD"],
        ["Sound:", "TBD"]
      ];

      doc.setFontSize(12);
      techSpecs.forEach(([label, value]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(label, margin, yPosition);
        doc.setFont('helvetica', 'normal');
        doc.text(value, margin + 40, yPosition);
        yPosition += 8;
      });

      // MUSIC CREDITS (if available)
      if (pressKitData.musicCredits) {
        yPosition += 10;
        addSection("MUSIC CREDITS", pressKitData.musicCredits);
      }

      const filename = `${filmData.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'press_kit'}_${selectedOrientation}.pdf`;
      doc.save(filename);
      
      setNotification({ 
        type: 'success', 
        message: `Professional ${selectedOrientation} press kit generated successfully!` 
      });

    } catch (error) {
      setNotification({ 
        type: 'error', 
        message: `PDF generation failed: ${error.message}` 
      });
      console.error("Professional PDF Error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-6">
        <h3 className="text-lg font-bold mb-4 text-blue-300 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Press Kit Configuration
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-zinc-300 mb-3">
              Page Orientation
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {orientationOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSelectedOrientation(option.id)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedOrientation === option.id
                      ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                      : 'border-zinc-600 bg-zinc-800/50 text-zinc-400 hover:border-blue-500/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{option.icon}</span>
                    <div>
                      <p className="font-semibold">{option.label}</p>
                      <p className="text-xs opacity-75">{option.dimensions}</p>
                    </div>
                  </div>
                  <p className="text-xs">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-zinc-300 mb-3">
              Include Sections
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {sectionOptions.map((section) => {
                const Icon = section.icon;
                return (
                  <label
                    key={section.id}
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      section.required
                        ? 'border-gray-500 bg-gray-500/20 text-gray-400 cursor-not-allowed'
                        : localSelectedSections[section.id]
                        ? 'border-green-500 bg-green-500/20 text-green-300'
                        : 'border-zinc-600 bg-zinc-800/50 text-zinc-400 hover:border-green-500/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={localSelectedSections[section.id]}
                      onChange={() => handleSectionToggle(section.id)}
                      disabled={section.required}
                      className="sr-only"
                    />
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{section.label}</span>
                    {section.required && (
                      <span className="text-xs text-gray-500">(Required)</span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-zinc-800/50 rounded-lg p-4">
        <h4 className="font-semibold text-zinc-300 mb-2">Press Kit Preview</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-zinc-400">
              <span className="font-medium">Orientation:</span> {orientationOptions.find(o => o.id === selectedOrientation)?.label}
            </p>
            <p className="text-zinc-400">
              <span className="font-medium">Pages:</span> Estimated 4-8 pages
            </p>
          </div>
          <div>
            <p className="text-zinc-400">
              <span className="font-medium">Sections:</span> {Object.values(localSelectedSections).filter(Boolean).length} included
            </p>
            <p className="text-zinc-400">
              <span className="font-medium">Format:</span> Professional PDF
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={generateProfessionalPressKit}
          disabled={isExporting || !filmData?.title}
          className={`
            flex items-center gap-3 px-8 py-4 rounded-lg font-semibold text-lg transition-all
            ${isExporting
              ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-lg hover:shadow-xl'
            }
          `}
        >
          {isExporting ? (
            <>
              <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
              Generating Press Kit...
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              Generate {selectedOrientation.charAt(0).toUpperCase() + selectedOrientation.slice(1)} Press Kit
            </>
          )}
        </button>
      </div>

      {!filmData?.title && (
        <p className="text-yellow-400 text-sm text-center">
          Please save your project data before generating a press kit.
        </p>
      )}
    </div>
  );
};

// Add this helper function outside the component if you don't have it imported
// or import your existing checkout logic if it's in a utility file.
const handlePendingCheckoutRedirect = async (user) => {
  try {
    // 1. Get the plan they selected from session storage
    // We need to pass this info. You might need to store 'pendingPlan' 
    // in sessionStorage in LandingSite.js as well, e.g. 'blackbox_pending_plan_cycle'
    const cycle = sessionStorage.getItem('blackbox_pending_plan_cycle') || 'monthly';
    const priceId = cycle === 'monthly' 
        ? 'price_1SrREDKHy89O5QxfQQB4HIZA' 
        : 'price_1SrREBKHy89O5QxfRHoErlko';

    // 2. Call API
    const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            priceId, 
            userId: user.uid,
            userEmail: user.email 
        }),
    });

    const data = await response.json();
    
    // 3. Redirect
    if (data.url) {
        window.location.href = data.url;
    } else {
        // If fails, remove flags so they can access the app
        sessionStorage.removeItem('blackbox_pending_checkout');
        sessionStorage.removeItem('blackbox_pending_plan_cycle');
        window.location.reload();
    }
  } catch (error) {
     console.error("Redirect failed", error);
     sessionStorage.removeItem('blackbox_pending_checkout');
     window.location.reload();
  }
};

// 1. Public Dossier Wrapper
// Extracts the projectId from the URL using the Router hook
const PublicDossierWrapper = ({ db }) => {
  const { projectId } = useParams();
  const [notification, setNotification] = useState({ type: '', message: '' });

  return (
    <>
      <Notification message={notification.message} type={notification.type} onDismiss={() => setNotification({ type: '', message: '' })} />
      <PublicDossierPage 
        projectId={projectId} 
        db={db} 
        setNotification={setNotification} 
      />
    </>
  );
};

// --- NEW HELPER COMPONENTS (MUST BE OUTSIDE LANDINGSITE) ---

const NavLink = ({ target, children, navigate }) => (
    <a href={`#${target}`} onClick={(e) => { e.preventDefault(); navigate(target); }} className="hover:text-brand-purple transition-colors">{children}</a>
);

const SignInModal = ({ isOpen, onClose, onSignIn }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await onSignIn(email, password);
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-md w-full">
                <h3 className="text-xl font-bold font-poppins text-white text-center mb-6">Sign In</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <AppInput type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
                    <AppInput type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
                    <AppButton type="submit" className="w-full" size="lg" disabled={loading}>
                        {loading ? 'Signing In...' : 'Sign In'}
                    </AppButton>
                </form>
                <div className="text-center mt-4">
                    <AppButton variant="ghost" onClick={onClose}>Cancel</AppButton>
                </div>
            </AppCard>
        </div>
    );
};

// --- CAMPAIGN FORM MODAL ---
const CampaignFormModal = ({ isOpen, onClose, onSave, campaign, baseConcept, filmData, audienceAnalysis, setNotification, db, storage }) => {
  const [formData, setFormData] = useState({ title: '', content: '', platform: 'instagram', status: 'Draft', creative: null, conceptName: '', hypothesis: '' });
  const [isUploading, setIsUploading] = useState(false);

  const customGraphics = useMemo(() => filmData?.custom_graphics || [], [filmData]);

  useEffect(() => {
    const initialData = { title: '', content: '', platform: 'instagram', status: 'Draft', creative: null, conceptName: '', hypothesis: '' };
    if (campaign) {
      setFormData({ ...initialData, ...campaign });
    } else if (baseConcept) {
      setFormData({ ...initialData, ...baseConcept });
    } else {
      setFormData(initialData);
    }
  }, [campaign, baseConcept, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  // --- BULLETPROOF BINARY UPLOAD ---
  const handleImageUpload = async (base64Image) => {
    if (!filmData?.id) {
        setNotification({ type: 'error', message: 'Save your project first to enable image uploads.' });
        return;
    }

    setIsUploading(true);
    setNotification({ type: 'info', message: 'Optimizing and uploading creative...' });

    try {
        // 1. Convert Base64 to Binary Blob (Professional Standard)
        const response = await fetch(base64Image);
        const blob = await response.blob();
        
        // 2. Define Path
        const fileName = `creative_${Date.now()}.png`;
        const storageRef = ref(storage, `${filmData.id}/campaigns/${fileName}`);

        // 3. Upload as Bytes (Stable for large files)
        await uploadBytes(storageRef, blob);
        const downloadUrl = await getDownloadURL(storageRef);

        setFormData(prev => ({ ...prev, creative: { url: downloadUrl } }));
        setNotification({ type: 'success', message: 'Creative asset secured in cloud.' });
    } catch (error) {
        console.error("Campaign Upload Error:", error);
        setNotification({ type: 'error', message: 'Upload failed. Please try a smaller image.' });
    } finally {
        setIsUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!filmData?.id) {
      setNotification({ type: 'error', message: 'Active project required to save campaigns.' });
      return;
    }
    onSave(formData);
  };

  const selectFromLibrary = (graphic) => {
      setFormData(prev => ({...prev, creative: { url: graphic.url }}));
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <AppCard className="p-8 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <h3 className="text-xl font-bold font-poppins mb-6 text-white">{campaign ? 'Edit Campaign' : 'Create New Campaign'}</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 overflow-y-auto pr-4 flex-grow">
          <form id="campaign-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-semibold text-sm mb-1 block text-zinc-400">Campaign Title</label>
              <AppInput name="title" value={formData.title} onChange={handleChange} placeholder="e.g., Summer Blockbuster Retargeting" required/>
            </div>
            <div>
              <label className="font-semibold text-sm mb-1 block text-zinc-400">Ad Copy</label>
              <AppTextarea name="content" value={formData.content} onChange={handleChange} placeholder="Write your hook..." rows={6} required/>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-semibold text-sm mb-1 block text-zinc-400">Platform</label>
                <AppSelect name="platform" value={formData.platform} onChange={handleChange}>{Object.keys(PLATFORMS).map(p => <option key={p} value={p}>{PLATFORMS[p].name}</option>)}</AppSelect>
              </div>
              <div>
                <label className="font-semibold text-sm mb-1 block text-zinc-400">Status</label>
                <AppSelect name="status" value={formData.status} onChange={handleChange}><option>Draft</option><option>Scheduled</option><option>Live</option></AppSelect>
              </div>
            </div>
          </form>

          <div className="space-y-6">
            <div className="bg-zinc-900/50 p-4 rounded-xl border border-zinc-800">
                <h4 className="font-semibold text-white mb-2 text-sm">Campaign Creative</h4>
                <div className="aspect-video bg-black rounded-lg border border-zinc-700 overflow-hidden mb-4 flex items-center justify-center">
                    {formData.creative?.url ? (
                        <img src={formData.creative.url} className="w-full h-full object-contain" alt="preview" />
                    ) : (
                        <ImageIcon className="w-12 h-12 text-zinc-800" />
                    )}
                </div>
                
                <h5 className="font-bold text-[10px] text-zinc-500 uppercase tracking-widest mb-2">From Library</h5>
                {customGraphics.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto mb-4">
                        {customGraphics.map(g => (
                            <button key={g.id} onClick={() => selectFromLibrary(g)} className={`aspect-square rounded border-2 transition-all ${formData.creative?.url === g.url ? 'border-purple-500' : 'border-transparent'}`}>
                                <img src={g.url} alt="library" className="w-full h-full object-cover rounded-sm"/>
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="text-[10px] text-zinc-600 mb-4">No graphics found in library.</p>
                )}
                
                <ImageUploader label="Upload New" uploadedFile={null} onFileSelect={handleImageUpload} setNotification={setNotification} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-zinc-800 mt-6 flex-shrink-0">
          <AppButton type="button" onClick={onClose} variant="secondary">Cancel</AppButton>
          <AppButton 
            type="submit" 
            form="campaign-form" 
            disabled={!formData.title || !formData.content || isUploading}
          >
            {isUploading ? 'Uploading...' : 'Save Campaign'}
          </AppButton>
        </div>
      </AppCard>
    </div>
  );
};

const ProjectSwitcher = ({ projects, activeProject, onSelectProject, onCreateNew, isCollapsed }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const wrapperRef = useRef(null);
    useEffect(() => {
      function handleClickOutside(event) {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);
    
    if (isCollapsed) return null;

    const handleProjectSelect = async (projectId) => {
      setIsLoading(true);
      await onSelectProject(projectId);
      setIsLoading(false);
      setIsOpen(false);
    };
    
    const handleNewProject = () => {
      onCreateNew();
      setIsOpen(false);
    };
    
    // FIX: A more robust helper function to format the date correctly.
    const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
        // Handle Firebase Timestamp objects (which have a .toDate method)
        if (dateValue && typeof dateValue.toDate === 'function') {
            return dateValue.toDate().toLocaleDateString();
        }
        
        // Handle Firestore Timestamp with seconds/nanoseconds
        if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
            // Convert Firestore timestamp to JS Date
            const date = new Date(dateValue.seconds * 1000);
            return date.toLocaleDateString();
        }
        
        // Handle standard JavaScript Date objects
        if (dateValue instanceof Date) {
            return dateValue.toLocaleDateString();
        }
        
        // Handle ISO string dates
        if (typeof dateValue === 'string') {
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString();
            }
        }
        
        // Handle timestamp numbers (milliseconds since epoch)
        if (typeof dateValue === 'number') {
            const date = new Date(dateValue);
            return date.toLocaleDateString();
        }
        
    } catch (error) {
        console.error('Error formatting date:', error, dateValue);
        return 'N/A';
    }
    
    // Fallback for any other format
    return 'N/A';
};


    return (
      <div ref={wrapperRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between bg-zinc-800/50 hover:bg-zinc-700/50 px-3 py-2 rounded-lg text-left transition-colors"
          disabled={isLoading}
        >
          <div className="flex items-center gap-3">
            <Box className="w-5 h-5 text-purple-400" />
            <div className="flex-grow min-w-0">
              <p className="text-xs text-zinc-400">Active Project</p>
              <p className="font-semibold text-white truncate">
                {isLoading ? 'Loading...' : (activeProject?.title || "Create a New Project")}
              </p>
            </div>
          </div>
          {isLoading ? (
            <BlackboxLoaderIcon className="w-4 h-4 text-purple-400" />
          ) : (
            <ChevronsUpDown className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          )}
        </button>
        {isOpen && !isLoading && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-zinc-800 border border-zinc-700 rounded-lg shadow-2xl z-40 overflow-hidden animate-fade-in-down">
            <div className="max-h-60 overflow-y-auto">
              {projects.map(project => (
                <button
                  key={project.id}
                  onClick={() => handleProjectSelect(project.id)}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-3 border-b border-zinc-700/50 last:border-b-0 ${activeProject?.id === project.id ? 'bg-purple-600 text-white' : 'text-zinc-300 hover:bg-purple-600/20 hover:text-purple-300'}`}
                >
                  {activeProject?.id === project.id ? (
                    <CheckCircle size={16} className="text-green-400" />
                  ) : (
                    <div className="w-4"></div>
                  )}
                  <div className="flex-grow min-w-0">
                    <p className="font-semibold truncate">{project.title || 'Untitled Project'}</p>
                    <p className="text-xs opacity-75">
                      {/* FIX: Use the new helper function for robust date formatting. */}
                      {project.genre} • Updated {formatDate(project.updated_at)}
                    </p>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={handleNewProject}
              className="w-full text-left px-4 py-3 text-sm transition-colors text-green-400 hover:bg-green-500/20 flex items-center gap-3 border-t border-zinc-700"
            >
              <Plus size={16} />
              <span className="font-semibold">Create New Project</span>
            </button>
          </div>
        )}
      </div>
    );
  };


// --- MAIN APP COMPONENT ---
const App = ({ user, db, storage, auth, notification, setNotification }) => {
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('blackbox_activeTab') || 'audience');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [openSections, setOpenSections] = useState({ coreIntake: true, socialPlan: true, appendix: true, reporting: true });
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isManualProjectSwitch, setIsManualProjectSwitch] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [showOnboarding, setShowOnboarding] = useState(false);

  // 1. INITIALIZE CREDIT SYSTEM
  const { remaining: credits, limit: maxCredits, isPro, deductCredit } = useCreditSystem(user, db);

  // 2. USER PROFILE STATE FOR SETTINGS
  const [userProfile, setUserProfile] = useState(null);

  const [filmData, setFilmData] = useState(() => {
    try {
      const persistedData = sessionStorage.getItem('blackbox_activeProjectState');
      return persistedData ? JSON.parse(persistedData) : null;
    } catch (error) {
      console.error("Failed to parse persisted state from sessionStorage", error);
      return null;
    }
  });

  const [campaigns, setCampaigns] = useState([]);
  const [influencerList, setInfluencerList] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [baseConceptForModal, setBaseConceptForModal] = useState(null);
const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);
const [isRefillModalOpen, setIsRefillModalOpen] = useState(false);
const [isRefillLoading, setIsRefillLoading] = useState(false);

// --- NEW: FULL USER PROFILE STATE ---
  // We need to store the full firestore document to get customerId/subscriptionId
  const [fullUserProfile, setFullUserProfile] = useState(null);

  // --- NEW: LISTEN TO USER PROFILE CHANGES ---
  useEffect(() => {
    if (!user || !db) return;

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        setFullUserProfile(docSnap.data());
      }
    });

    return () => unsubscribe();
  }, [user, db]);

// --- NEW EFFECT: CHECK ONBOARDING STATUS ---
  useEffect(() => {
      if (user && db) {
          const userRef = doc(db, 'users', user.uid);
          // Listen to user document to see if they have onboarded
          const unsub = onSnapshot(userRef, (docSnap) => {
              if (docSnap.exists()) {
                  const userData = docSnap.data();
                  // If the flag is missing or false, show onboarding
                  if (!userData.hasCompletedOnboarding) {
                      setShowOnboarding(true);
                  } else {
                      setShowOnboarding(false);
                  }
              } else {
                  // If user doc doesn't exist yet (fresh signup), create it and show onboarding
                  setDoc(userRef, { 
                      email: user.email,
                      createdAt: new Date(),
                      hasCompletedOnboarding: false 
                  }, { merge: true });
                  setShowOnboarding(true);
              }
          });
          return () => unsub();
      }
  }, [user, db]);

  // --- DEV TOOL: UNLIMITED POWER ---
const handleDevRefill = async () => {
    if (!user) return;
    
    // Double security check (Frontend)
    if (user.email !== 'iamwesmiller@gmail.com') return;

    try {
        const userRef = doc(db, 'users', user.uid);
        
        // Updates your user record directly in Firestore
        await updateDoc(userRef, {
            creditsRemaining: 10000, // 10k credits
            monthlyLimit: 10000,     // High limit
            isPro: true              // Force Pro status
        });
        
        setNotification({ type: 'success', message: '⚡ GOD MODE: Credits Refilled' });
    } catch (error) {
        console.error("Dev Refill Failed:", error);
        setNotification({ type: 'error', message: 'Dev Action Failed' });
    }
};

  const handleRefillPurchase = async () => {
    if (!user) return;
    setIsRefillLoading(true);

    // YOUR NEW STRIPE PRICE ID
    const refillPriceId = 'price_1SrRDuKHy89O5QxfMGZsCAxo';

    try {
        const response = await fetch('/api/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                priceId: refillPriceId, 
                userId: user.uid, 
                userEmail: user.email,
                mode: 'payment' // Explicitly tell backend this is one-time if needed
            }),
        });

        const data = await response.json();

        if (data.url) {
            window.location.href = data.url; 
        } else {
            throw new Error('No checkout URL returned');
        }
    } catch (error) {
        console.error("Refill Error:", error);
        setNotification({ type: 'error', message: 'Refill checkout failed. Please try again.' });
        setIsRefillLoading(false);
    }
};

// --- ADD THIS TO APP.JS (Around line 1239) ---

const handleUpdateProject = useCallback(async (updatedProject) => {
    if (!user || !db) return;

    try {
        // 1. Prepare Data
        const projectDocRef = doc(db, 'film_data', updatedProject.id);
        const cleanData = deepSanitizeForFirestore({ ...updatedProject, updated_at: new Date() });

        // 2. Update Firestore
        await updateDoc(projectDocRef, cleanData);

        // 3. Update the Portfolio List (Immediate UI feedback)
        setProjects(prev => prev.map(p => p.id === updatedProject.id ? { ...p, ...cleanData } : p));

        // 4. If this project is currently "Active" in the sidebar, update that state too
        if (activeProject?.id === updatedProject.id) {
            setFilmData(prev => ({ ...prev, ...cleanData }));
            setActiveProject(prev => ({ ...prev, ...cleanData }));
        }

    } catch (error) {
        console.error("Update failed:", error);
        throw error; // This allows ProjectDashboard to catch it and show the error toast
    }
}, [user, db, activeProject]);

  const handleDeleteProject = async (projectId) => {
    if (!user || !db) return;
    
    try {
      // 1. Delete from Firestore
      await deleteDoc(doc(db, 'film_data', projectId));
      
      // 2. Update Local State
      setProjects(prev => prev.filter(p => p.id !== projectId));
      
      // 3. Handle Active Project
      if (activeProject?.id === projectId) {
        setActiveProject(null);
        setFilmData(null);
        sessionStorage.removeItem('blackbox_activeProjectState');
        setActiveTab('dashboard'); // Or whatever your home tab is
      }
      
      setNotification({ type: 'success', message: 'Project deleted successfully.' });
    } catch (error) {
      console.error("Error deleting project:", error);
      setNotification({ type: 'error', message: 'Failed to delete project.' });
    }
  };

const UpgradeModal = ({ isOpen, onClose, currentPlan, onSelectPlan, isProcessing }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-8 max-w-md w-full shadow-2xl">
                <h3 className="text-2xl font-bold text-white font-poppins mb-2 text-center">
                    Upgrade Your Plan
                </h3>
                <p className="text-zinc-400 text-center mb-8">
                    Unlock 50 credits per month and full access to all AI tools.
                </p>

                <div className="space-y-4">
                    {/* Monthly Option */}
                    <button
                        onClick={() => onSelectPlan('monthly')}
                        disabled={isProcessing}
                        className="w-full p-4 rounded-lg border border-zinc-700 bg-zinc-800/50 hover:border-purple-500 hover:bg-zinc-800 transition-all flex justify-between items-center group"
                    >
                        <div className="text-left">
                            <p className="font-bold text-white">Monthly</p>
                            <p className="text-sm text-zinc-400">$199 / month</p>
                        </div>
                        <div className="w-6 h-6 rounded-full border-2 border-zinc-600 group-hover:border-purple-500 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    </button>

                    {/* Yearly Option */}
                    <button
                        onClick={() => onSelectPlan('yearly')}
                        disabled={isProcessing}
                        className="w-full p-4 rounded-lg border-2 border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition-all flex justify-between items-center relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 bg-purple-500 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg">
                            BEST VALUE
                        </div>
                        <div className="text-left">
                            <p className="font-bold text-white">Yearly</p>
                            <p className="text-sm text-zinc-300">$1199 / year</p>
                            <p className="text-xs text-green-400 font-bold mt-1">Save 20%</p>
                        </div>
                        <div className="w-6 h-6 rounded-full border-2 border-purple-500 flex items-center justify-center">
                             <div className="w-3 h-3 rounded-full bg-purple-500 opacity-0 hover:opacity-100 transition-opacity" />
                        </div>
                    </button>
                </div>

                <div className="mt-8 flex justify-center">
                    <button onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-300">
                        Cancel
                    </button>
                </div>
                
                {isProcessing && (
                    <p className="text-center text-xs text-purple-400 mt-4 animate-pulse">
                        Redirecting to secure checkout...
                    </p>
                )}
            </div>
        </div>
    );
};

  // --- HANDLE UPGRADE CLICK (Connects to your API) ---
  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(true);
};

const handlePlanSelection = async (planType) => {
    if (!user) return;
    setIsUpgradeLoading(true);

    // 1. Get the correct ID based on user choice
    const priceId = planType === 'monthly' 
        ? 'price_1SrREDKHy89O5QxfQQB4HIZA' 
        : 'price_1SrREBKHy89O5QxfRHoErlko';

    try {
        const response = await fetch('/api/checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                priceId: priceId, 
                userId: user.uid,
                userEmail: user.email 
            }),
        });

        const data = await response.json();

        if (data.url) {
            window.location.href = data.url; 
        } else {
            throw new Error('No checkout URL returned');
        }
    } catch (error) {
        console.error("Checkout Error:", error);
        setNotification({ type: 'error', message: 'Checkout failed. Please try again.' });
        setIsUpgradeLoading(false); // Only stop loading on error (otherwise we redirect)
    }
};

  useEffect(() => {
    if (filmData) {
      try {
        sessionStorage.setItem('blackbox_activeProjectState', JSON.stringify(filmData));
      } catch (error) {
        console.error("Failed to save state to sessionStorage", error);
      }
    }
  }, [filmData]);

  useEffect(() => {
    localStorage.setItem('blackbox_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeProject && activeProject.id) {
      localStorage.setItem('blackbox_activeProjectId', activeProject.id);
    }
  }, [activeProject]);

const loadProject = useCallback(async (project) => {
    if (!project || !db || !user) return; // Ensure user is defined

    setActiveProject(project);
    setFilmData(project);

    try {
      // FIX: Query by BOTH film_id and user_id to satisfy security rules
      const qCampaigns = query(
        collection(db, 'campaigns'), 
        where('film_id', '==', project.id),
        where('user_id', '==', user.uid)
      );
      
      const campaignDocs = await getDocs(qCampaigns);
      setCampaigns(campaignDocs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (campaignError) {
      console.warn('Could not load campaigns:', campaignError);
      setCampaigns([]); 
    }

    try {
      const qInfluencers = query(collection(db, 'influencers'), where('user_id', '==', user.uid));
      const influencerDocs = await getDocs(qInfluencers);
      setInfluencerList(influencerDocs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (influencerError) {
      console.warn('Could not load influencers:', influencerError);
      setInfluencerList([]); 
    }
  }, [db, user]);

 useEffect(() => {
    const loadInitialData = async () => {
      if (!user || !db) return;
      setIsLoadingData(true);

      const qProjects = query(collection(db, 'film_data'), where('user_id', '==', user.uid));
      const projectDocs = await getDocs(qProjects);
      const allProjects = projectDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProjects(allProjects);

      if (!isManualProjectSwitch && allProjects.length > 0) {
        const lastProjectId = localStorage.getItem('blackbox_activeProjectId');
        const projectToLoad = allProjects.find(p => p.id === lastProjectId) || allProjects[0];

        // FIX: The project data is now loaded from Firestore every time,
        // and the session storage is updated with the fresh data.
        if (!filmData || filmData.id !== projectToLoad.id) {
          setActiveTab('audience');
          setOpenSections({ coreIntake: true, socialPlan: true, appendix: true, reporting: true });
          await loadProject(projectToLoad);
          sessionStorage.setItem('blackbox_activeProjectState', JSON.stringify(projectToLoad));
        } else {
          // If the project in session storage is the same as the last used,
          // just re-load the associated data.
          await loadProject(filmData);
        }
      } else if (!isManualProjectSwitch && allProjects.length === 0 && !filmData) {
        handleCreateNewProjectClick();
      }
      setIsLoadingData(false);
    };

    loadInitialData();
  }, [user, db, isManualProjectSwitch, loadProject]);

const deepSanitizeForFirestore = (obj) => {
    // Null or primitive values - return as-is
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    // CRITICAL: Preserve Date objects for Firestore timestamps
    if (obj instanceof Date) {
      return obj;
    }

    // CRITICAL: Preserve Firestore Timestamp objects
    if (obj && typeof obj.toDate === 'function') {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj
        .map(item => deepSanitizeForFirestore(item))
        .filter(item => item !== undefined);
    }

    // Handle plain objects
    const sanitized = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (value !== undefined) {
          const sanitizedValue = deepSanitizeForFirestore(value);
          if (sanitizedValue !== undefined) {
            sanitized[key] = sanitizedValue;
          }
        }
      }
    }
    return sanitized;
};

const saveFilmData = useCallback(async (dataToSave, options = {}) => {
    if (!user || !db) {
      setNotification({ type: 'error', message: 'Not logged in.' });
      return null;
    }
    let savedData;
    const isNewProject = !dataToSave.id;

    if (isNewProject) {
      // 1. Prepare Data
      const newProjectData = { 
        ...dataToSave, 
        user_id: user.uid, 
        created_at: new Date(), 
        updated_at: new Date() 
      };
      delete newProjectData.id;

      // 2. Write to Firestore
      const cleanData = deepSanitizeForFirestore(newProjectData); 
      const docRef = await addDoc(collection(db, 'film_data'), cleanData);
      
      // 3. Construct Local Object
      savedData = { id: docRef.id, ...cleanData };

      // 4. Update Projects List & State
      setProjects(prev => [savedData, ...prev]);
      setActiveProject(savedData);
      setFilmData(savedData);
      setCampaigns([]);      
      setInfluencerList([]); 

      // KEEP: Notify on creation
      if (!options.silent) {
          setNotification({ type: 'success', message: 'Project created successfully!' });
      }
    } else {
      // --- UPDATE LOGIC ---
      const projectDocRef = doc(db, 'film_data', dataToSave.id);
      const updatedProjectData = { ...dataToSave, updated_at: new Date() };
      
      const cleanData = deepSanitizeForFirestore(updatedProjectData); 
      await updateDoc(projectDocRef, cleanData);
      
      savedData = { ...updatedProjectData };
      
      setFilmData(savedData);
      setActiveProject(savedData);
      setProjects(prev => prev.map(p => p.id === savedData.id ? savedData : p));
      
      // CHANGE: Silent by default for updates (No notification)
    }
    return savedData;
  }, [user, db, setNotification, activeTab]);

  const resetFilmData = useCallback(async (dataKeys) => {
    if (!activeProject?.id) return;

    // Create a copy of the current filmData
    const updatedData = { ...filmData };
    
    // Set the specified keys to null to clear the data
    dataKeys.forEach(key => {
        updatedData[key] = null;
    });

    // Save the modified filmData object back to Firestore
    await saveFilmData(updatedData);

    // Update the local state to reflect the changes
    setFilmData(updatedData);
}, [activeProject, filmData, saveFilmData]);
  
 const saveFilmField = useCallback(async (field, value, projectId = activeProject?.id) => {
    if (!user || !db || !projectId) {
      // Keep errors visible
      setNotification({ type: 'error', message: 'Not logged in or no active project.' });
      return null;
    }
    const projectDocRef = doc(db, 'film_data', projectId);
    await updateDoc(projectDocRef, { [field]: value, updated_at: new Date() });
    
    const docSnap = await getDoc(projectDocRef);
    const savedData = { id: docSnap.id, ...docSnap.data() };
    setFilmData(savedData);
    
    // CHANGE: Commented out the success notification to keep the UI clean
    // setNotification({ type: 'success', message: 'Project saved!' });
    
    return savedData;
  }, [user, db, activeProject, setNotification]);

 const handleSelectProject = useCallback(async (projectId) => {
    const selected = projects.find(p => p.id === projectId);
    
    // CASE A: Switching to a different project
    if (selected && selected.id !== activeProject?.id) {
      setIsManualProjectSwitch(true);
      setIsLoadingData(true);
      setActiveTab('audience'); // Default to workspace
      setOpenSections({ coreIntake: true, socialPlan: true, appendix: true, reporting: true });
      setCampaigns([]);
      setInfluencerList([]);
      setIsCampaignModalOpen(false);
      setEditingCampaign(null);
      setBaseConceptForModal(null);
      setConfirmModal({ isOpen: false });
      sessionStorage.removeItem('blackbox_activeProjectState');
      await loadProject(selected);
      setIsLoadingData(false);
      setIsManualProjectSwitch(false);
    }
    // CASE B: Clicking the ALREADY active project (Fixes "Stuck" bug)
    else if (selected && selected.id === activeProject?.id) {
        // If we are on the dashboard, force entry into the workspace
        if (activeTab === 'dashboard') {
            setActiveTab('audience'); 
        }
    }
  }, [projects, activeProject, loadProject, activeTab, setNotification]);

  // 1. Opens the Modal (Does not create data yet)
  const handleCreateNewProjectClick = () => {
    setIsCreateModalOpen(true);
  };

  // 2. Actually creates the project (Only runs when "Create" is clicked in modal)
  const executeCreateProject = async (details, posterFile) => {
      setIsCreatingProject(true);
      try {
        let posterUrl = null;
        if (posterFile) {
             const path = `users/${user.uid}/project_posters`;
             const fileName = `poster_${Date.now()}_${posterFile.name}`;
             posterUrl = await uploadImageToFirebase(posterFile, storage, path, fileName);
        }

        const newProjectData = {
            ...details,
            poster_a: posterUrl,
            ai_analyses: [],
            press_kit: { cast: [], crew: [], full_credits: [] },
            is_dossier_public: false
        };

        const saved = await saveFilmData(newProjectData);
        if (saved) {
            setIsCreateModalOpen(false);
            setActiveTab('audience'); // Auto-navigate to workspace
        }
      } catch (error) {
          console.error("Creation failed", error);
          setNotification({ type: 'error', message: 'Failed to create project.' });
      } finally {
          setIsCreatingProject(false);
      }
  };

 const addCampaign = useCallback(async (campaignData) => {
    if (!user || !db || !activeProject) {
      setNotification({ type: 'error', message: 'Cannot add campaign without an active project.' });
      return;
    }

    // 1. Prepare Data
    const rawCampaign = { 
        ...campaignData, 
        film_id: activeProject.id, 
        user_id: user.uid, 
        created_at: new Date() 
    };

    // 2. Sanitize (Removes undefined values that cause Firestore crashes)
    const newCampaign = deepSanitizeForFirestore(rawCampaign);

    try {
        // 3. Save to DB
        const docRef = await addDoc(collection(db, 'campaigns'), newCampaign);
        const docSnap = await getDoc(docRef);
        const savedData = { id: docSnap.id, ...docSnap.data() };
        
        // 4. Update Local State immediately
        setCampaigns(prev => [savedData, ...prev]);
        
        return savedData;
    } catch (error) {
        console.error("Add Campaign Error:", error);
        setNotification({ type: 'error', message: 'Failed to save campaign.' });
        return null;
    }
  }, [user, db, activeProject, setNotification]);

  const updateCampaign = useCallback(async (campaignId, updatedData) => {
    const campaignDocRef = doc(db, 'campaigns', campaignId);
    await updateDoc(campaignDocRef, updatedData);
    const docSnap = await getDoc(campaignDocRef);
    const data = { id: docSnap.id, ...docSnap.data() };
    setCampaigns(prev => prev.map(c => c.id === campaignId ? data : c));
    setNotification({ type: 'success', message: 'Campaign updated.' });
  }, [db, setNotification]);

  const deleteCampaign = useCallback(async (campaignId) => {
    const campaignDocRef = doc(db, 'campaigns', campaignId);
    await deleteDoc(campaignDocRef);
    setCampaigns(prev => prev.filter(c => c.id !== campaignId));
    setNotification({ type: 'info', message: 'Campaign deleted.' });
  }, [db, setNotification]);

  const addInfluencer = useCallback(async (influencerData) => {
    if (!user || !db) return;
    const newInfluencer = { ...influencerData, user_id: user.uid, created_at: new Date() };
    const docRef = await addDoc(collection(db, 'influencers'), newInfluencer);
    const docSnap = await getDoc(docRef);
    const data = { id: docSnap.id, ...docSnap.data() };
    setInfluencerList(prev => [data, ...prev]);
    setNotification({ type: 'success', message: 'Influencer added to list!' });
  }, [user, db, setNotification]);

  const openCampaignModal = useCallback((data, isNew = false) => {
    if (isNew) {
        // User clicked "New Campaign" for a blank form
        setEditingCampaign(null);
        setBaseConceptForModal(null);
    } else if (data && data.id) {
        // Editing an existing campaign that has an ID
        setEditingCampaign(data);
        setBaseConceptForModal(null);
    } else if (data) {
        // Creating a new campaign from a concept (like from Remix Studio)
        // It has data but no ID yet.
        setEditingCampaign(null);
        setBaseConceptForModal(data);
    } else {
        // Fallback for safety
        setEditingCampaign(null);
        setBaseConceptForModal(null);
    }
    setIsCampaignModalOpen(true);
}, []);

  const handleSaveCampaign = async (campaignData) => {
    if (editingCampaign) {
      await updateCampaign(editingCampaign.id, campaignData);
    } else {
      const data = await addCampaign(campaignData);
      if (data) {
        setCampaigns(prev => [data, ...prev]);
        setNotification({ type: 'success', message: 'Campaign created successfully!' });
      }
    }
    setIsCampaignModalOpen(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    sessionStorage.removeItem('blackbox_activeProjectState');
  };

  const handleDismissNotification = () => setNotification({ message: '', type: '' });

  const handleSectionToggle = (sectionKey, children) => {
    if (isSidebarCollapsed) {
      setActiveTab(children[0]);
      setOpenSections(prev => ({ ...prev, [sectionKey]: true }));
    } else {
      setOpenSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    }
  };

  const handleTabSwitch = useCallback((newTab) => {
    localStorage.setItem('blackbox_activeTab', newTab);
    setActiveTab(newTab);
    if (!filmData?.title && newTab === 'audience') {
      setNotification({
        type: 'info',
        message: 'Start by entering your film details and running an audience analysis.'
      });
    }
  }, [filmData, setNotification]);

  const allTabDetails = {
    dashboard: { label: 'Project Portfolio', icon: Film, component: ProjectDashboard },
    audience: { label: 'Audience Genius', icon: BrainCircuit, component: AudienceAnalysisTab },
    greenlight: { label: 'Greenlight Coverage', icon: TrafficCone, component: GreenlightCoverageTab },
    revenue: { label: 'Revenue Forecaster', icon: Banknote, component: RevenueForecasterTab },
    press: { label: 'PR Plan', icon: Megaphone, component: PressOutreachTab },
    video: { label: 'Video Analysis', icon: Video, component: VideoAnalysisTab },
    visuals: { label: 'Visual Content', icon: Palette, component: VisualContentTab },
    content: { label: 'Ad Concept Studio', icon: WandSparkles, component: ContentCreationTab },
    budget: { label: 'Mktg Budget Planner', icon: DollarSign, component: BudgetPlannerTab },
    campaigns:  { label: 'Campaign Manager',  icon: Briefcase, component: CampaignManagerTab },
    simulator:  { label: 'Reach Simulator',   icon: Zap,       component: AndromedaSimulatorTab },
    competition: { label: 'Competitive Intel', icon: PieChartIcon, component: CompetitiveIntelTab },
    remix: { label: 'Social Proof Studio', icon: Heart, component: RemixTab },
    analytics: { label: 'Performance', icon: BarChart2, component: AnalyticsTab },
    community: { label: 'Generate Campaign', icon: Radar, component: CampaignIntelligenceTab },
    dossier: { label: 'Dossier', icon: FileText, component: DossierTab },
    settings: { label: 'Settings', icon: Settings, component: SettingsTab },
  };

  // IA v2: everything now rolls up to two deliverables — Social Plan and PR Plan —
  // built from one Audience Genius intake. 'community' (Generate Campaign) is the
  // single entry point into the Social Plan; visuals/content/remix/simulator/campaigns
  // are its supporting asset & execution tabs, not standalone destinations.
  const tabs = {
    dashboard: { label: 'Portfolio', icon: Film, isTopLevel: true, key: 'dashboard' },
    coreIntake: {
        label: 'Core Intake',
        icon: Target,
        children: ['audience', 'greenlight'] // single mandatory data-entry point + optional script verdict
    },
    socialPlan: {
        label: 'Social Plan',
        icon: PenSquare,
        children: ['community', 'video', 'visuals', 'content', 'remix', 'simulator', 'campaigns']
        // community = "Generate Campaign" (merged Story & Hooks / Character Profiler / Social Playbook)
        // the rest are supporting asset & execution tabs that feed the same plan
    },
    press: { label: 'PR Plan', icon: Megaphone, isTopLevel: true, key: 'press' },
    appendix: {
        label: 'Appendix Data',
        icon: PieChartIcon,
        children: ['revenue', 'budget', 'competition'] // optional supporting data, not folded into either plan
    },
    reporting: { label: 'Reporting', icon: AreaChart, children: ['analytics', 'dossier'] },
    settings: { label: 'Settings', isTopLevel: true, key: 'settings', icon: Settings },
  };

  const allDataForDossier = useMemo(() => ({
    filmData,
    campaigns,
    visualAnalysis: filmData?.visual_analysis,
    posterA: filmData?.poster_a,
    posterB: filmData?.poster_b,
    videoAnalysis: filmData?.video_analysis,
    budgetPlan: filmData?.budget_plan,
    revenueForecast: filmData?.revenue_forecast,
    pressStrategy: filmData?.press_strategy 
  }), [filmData, campaigns]);
  if (isLoadingData && !filmData) {

    return (
      <div className="dark font-poppins">
        <style>
          {`
          @keyframes whitePulseGlow {
            0%, 100% { stroke: #ffffff; text-shadow: 0 0 5px #ffffff, 0 0 10px #a855f7; }
            50% { stroke: #a78bfa; text-shadow: 0 0 10px #a78bfa, 0 0 20px #a78bfa; }
          }
          .neon-text-light {
            text-shadow: 0 0 3px #a78bfa, 0 0 7px #a78bfa;
          }
          `}
        </style>
        <div className="bg-zinc-950 min-h-screen flex flex-col items-center justify-center">
          <BlackboxLoaderIcon className="w-24 h-24" variant="white-pulse" />
          <h1 className="text-4xl font-bold text-white font-poppins mt-6">BLACKBOX <span className="text-xl uppercase text-purple-400 neon-text-light">mi</span></h1>
          <p className="text-zinc-400">Loading Your Projects...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="dark font-poppins bg-zinc-950 min-h-screen">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300&family=poppins:wght@400;700;900&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-poppins-light-italic { font-family: 'Poppins', sans-serif; font-weight: 300; font-style: italic; }
        /* Hide scrollbar for Chrome, Safari and Opera */
        .custom-scrollbar::-webkit-scrollbar { display: none; }
        /* Hide scrollbar for IE, Edge and Firefox */
        .custom-scrollbar { -ms-overflow-style: none;  scrollbar-width: none; }
      `}</style>

      {/* --- ONBOARDING & MODALS --- */}
      {showOnboarding && <OnboardingFlow user={user} db={db} onComplete={() => setShowOnboarding(false)} />}
      <Notification message={notification.message} type={notification.type} onDismiss={handleDismissNotification} />
      <ConfirmationModal isOpen={confirmModal.isOpen} title={confirmModal.title} message={confirmModal.message} onConfirm={confirmModal.onConfirm} onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })} />
      <CampaignFormModal isOpen={isCampaignModalOpen} onClose={() => setIsCampaignModalOpen(false)} onSave={handleSaveCampaign} campaign={editingCampaign} baseConcept={baseConceptForModal} filmData={filmData} audienceAnalysis={filmData?.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis} setNotification={setNotification} />
      <CreditPricingModal isOpen={isPricingModalOpen} onClose={() => setIsPricingModalOpen(false)} />
      <UpgradeModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} onSelectPlan={handlePlanSelection} isProcessing={isUpgradeLoading} />
      <RefillModal isOpen={isRefillModalOpen} onClose={() => setIsRefillModalOpen(false)} onConfirm={handleRefillPurchase} isProcessing={isRefillLoading} />
      <CreateProjectModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSave={executeCreateProject} isSaving={isCreatingProject} setNotification={setNotification} />

      {/* --- MAIN RESPONSIVE LAYOUT WRAPPER --- */}
      {/* Mobile: Column stack / Desktop: Row */}
      <div className="flex flex-col lg:flex-row min-h-screen text-zinc-200">
        
        {/* 1. MOBILE HEADER (Visible only on mobile) */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-white/5 bg-zinc-950/90 backdrop-blur-md sticky top-0 z-40">
            <div className="flex items-center gap-2">
                <BlackboxIcon className="w-6 h-6 text-white" />
                <span className="text-sm font-bold tracking-widest text-white">BLACKBOX</span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-zinc-400 hover:text-white transition-colors">
                <Menu size={24} />
            </button>
        </div>

        {/* 2. MOBILE MENU OVERLAY (Backdrop) */}
        {isMobileMenuOpen && (
            <div 
                className="fixed inset-0 bg-black/80 z-40 lg:hidden backdrop-blur-sm animate-in fade-in duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
            />
        )}

        {/* 3. SIDEBAR NAVIGATION */}
        <aside className={`
            fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 h-[100dvh]
            transform transition-transform duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            flex flex-col border-r border-white/5 
            bg-zinc-950/95 lg:bg-black/40 lg:backdrop-blur-xl
            ${isSidebarCollapsed ? 'lg:w-20 w-64' : 'lg:w-72 w-72'} 
            shadow-2xl lg:shadow-none
        `}>
          
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-5 mb-2 flex-shrink-0">
            <div className={`flex items-center gap-3 overflow-hidden ${isSidebarCollapsed ? 'w-8' : 'w-auto'}`}>
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-purple-600 blur-lg opacity-20" />
                <BlackboxIcon className="relative w-8 h-8 text-white" />
              </div>
              {!isSidebarCollapsed && (
                <div className="flex flex-col min-w-0">
                  <h1 className="text-lg font-bold tracking-tight text-white font-poppins leading-none">
                    BLACKBOX
                  </h1>
                  <span className="text-[10px] font-bold text-purple-400 tracking-[0.2em] uppercase">Intelligence</span>
                </div>
              )}
            </div>

            {/* Close Button (Mobile) */}
            <button onClick={() => setIsMobileMenuOpen(false)} className="lg:hidden text-zinc-400 hover:text-white">
                <X size={20} />
            </button>

            {/* Collapse Button (Desktop) */}
            <button 
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
                className="hidden lg:block text-zinc-600 hover:text-white transition-colors"
            >
                {isSidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
          </div>

          {/* Project Switcher */}
          <div className="px-4 mb-4 flex-shrink-0">
    {/* EXPANDED STATE */}
    <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'opacity-0 pointer-events-none h-0 overflow-hidden' : 'opacity-100'}`}>
        <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2 pl-1">
            Current Production
        </p>
        <ProjectSwitcher
            projects={projects}
            activeProject={activeProject}
            onSelectProject={handleSelectProject}
            onCreateNew={handleCreateNewProjectClick}
            isCollapsed={isSidebarCollapsed}
        />

        {/* Active Film Poster Strip — shown when expanded and a project is active */}
        {filmData?.title && (
            <div className="mt-3 flex items-center gap-2 px-1">
                <div className="w-8 h-11 rounded overflow-hidden flex-shrink-0 bg-zinc-900 border border-zinc-800 shadow-lg">
                   {filmData.poster_a ? (
    <img
        src={filmData.poster_a}
        alt={filmData.title}
        className="w-full h-full object-cover"
        onError={e => { e.target.style.display = 'none'; }}
    />
) : (
    <div className="w-full h-full flex items-center justify-center">
        <Film size={11} className="text-zinc-600" />
    </div>
)}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-zinc-300 truncate">{filmData.title}</p>
                    {filmData.genre && (
                        <p className="text-[10px] text-zinc-600 truncate">{filmData.genre} · {filmData.budgetLevel?.split(' ')[0]}</p>
                    )}
                </div>
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0 shadow-[0_0_6px_#22c55e]" title="Active" />
            </div>
        )}
    </div>

    {/* COLLAPSED STATE: show poster thumbnail or icon */}
    {isSidebarCollapsed && (
        <div className="w-10 h-14 mx-auto rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800 shadow-md hover:border-purple-500/40 transition-colors cursor-pointer"
             onClick={handleCreateNewProjectClick}
             title={filmData?.title ?? 'Projects'}
        >
            {filmData.poster_a ? (
    <img
        src={filmData.poster_a}
        alt={filmData.title}
        className="w-full h-full object-cover"
        onError={e => { e.target.style.display = 'none'; }}
    />
) : (
    <div className="w-full h-full flex items-center justify-center">
        <Film size={11} className="text-zinc-600" />
    </div>
)}
        </div>
    )}
</div>

          {/* Nav Links */}
          <nav className="flex-grow space-y-1 overflow-y-auto px-3 custom-scrollbar">
            {Object.entries(tabs).map(([key, { label, icon: Icon, children, isTopLevel }]) => {
              if (isTopLevel) {
                const isActive = activeTab === key;
                return (
                  <button 
                    key={key} 
                    onClick={() => { handleTabSwitch(key); setIsMobileMenuOpen(false); }} 
                    className={`group w-full flex items-center p-3 rounded-lg text-left transition-all duration-200 relative overflow-hidden ${isSidebarCollapsed ? 'justify-center' : ''} ${isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`} 
                    title={isSidebarCollapsed ? label : ''}
                  >
                    {isActive && <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500 shadow-[0_0_10px_#a855f7]" />}
                    {isActive && <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent pointer-events-none" />}
                    <Icon className={`w-5 h-5 flex-shrink-0 relative z-10 transition-colors ${isActive ? 'text-purple-400' : 'group-hover:text-zinc-300'} ${isSidebarCollapsed ? '' : 'mr-3'}`} />
                    {!isSidebarCollapsed && <span className="relative z-10 text-sm font-medium">{label}</span>}
                  </button>
                );
              }
              return (
                  <div key={key} className="mb-2 pb-2 border-b border-white/[0.04] last:border-0">
                    {!isSidebarCollapsed && (
                       <p className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest mt-4 transition-colors ${
                        children?.includes(activeTab) ? 'text-purple-500/70' : 'text-zinc-600'
                        }`}>
                            {label}
                              </p>
                    )}
                    <div className="space-y-0.5">
                        {children.map(childKey => {
                            const details = allTabDetails[childKey];
                            const isChildActive = activeTab === childKey;
                            return (
                                <button 
                                    key={childKey} 
                                    onClick={() => { handleTabSwitch(childKey); setIsMobileMenuOpen(false); }} 
                                    className={`group w-full flex items-center p-2.5 rounded-lg text-left transition-all duration-200 relative ${isSidebarCollapsed ? 'justify-center' : ''} ${isChildActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/5'}`} 
                                    title={isSidebarCollapsed ? details.label : ''}
                                >
                                    {isChildActive && <div className="absolute left-0 top-2 bottom-2 w-0.5 bg-purple-500 shadow-[0_0_8px_#a855f7]" />}
                                    <details.icon className={`w-4 h-4 flex-shrink-0 transition-colors ${isChildActive ? 'text-purple-400' : 'group-hover:text-zinc-300'} ${isSidebarCollapsed ? '' : 'mr-3'}`} />
                                    {!isSidebarCollapsed && <span className="text-sm">{details.label}</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          {/* Sidebar Footer */}
<div className="mt-auto pt-4 border-t border-white/5 bg-black/20 backdrop-blur-md pb-4 flex-shrink-0">
    {!isSidebarCollapsed && (
        <>
            <UsageMeter 
               remaining={credits} 
               limit={maxCredits} 
               isPro={isPro} 
               loading={false}
               onUpgrade={handleUpgradeClick}
               onRefill={() => setIsRefillModalOpen(true)}
               onShowPricing={() => setIsPricingModalOpen(true)}
            />
            
            {/* 👇 SECRET DEV BUTTON - ONLY FOR YOU 👇 */}
            {user?.email === 'iamwesmiller@gmail.com' && (
                <div className="px-4 mb-4">
                    <button 
                        onClick={handleDevRefill}
                        className="w-full py-2 text-[10px] font-mono uppercase tracking-widest bg-zinc-900 hover:bg-purple-900/50 text-zinc-600 hover:text-purple-300 border border-zinc-800 hover:border-purple-500/50 rounded transition-all flex items-center justify-center gap-2"
                    >
                        <Zap size={10} /> RECHARGE CREDITS
                    </button>
                </div>
            )}
        </>
    )}
              <div className="px-4">
                {user ? (
                  <div className={`flex items-center gap-3 p-2 rounded-xl transition-colors ${!isSidebarCollapsed ? 'bg-white/5 border border-white/5 hover:border-purple-500/30' : 'justify-center'}`}>
                    <div className="relative">
                        <img src={`https://placehold.co/40x40/27272a/a78bfa?text=${user.email.charAt(0).toUpperCase()}`} alt="User" className="w-8 h-8 rounded-lg object-cover ring-1 ring-white/10"/>
                        <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-black" />
                    </div>
                    {!isSidebarCollapsed && (
                      <div className="overflow-hidden flex-1 min-w-0">
                        <p className="text-xs font-bold text-white truncate">{user.email.split('@')[0]}</p>
                        <button onClick={handleLogout} className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 transition-colors">
                            <LogOut size={10} /> Sign Out
                        </button>
                        
                      </div>
                    )}
                  </div>
                ) : (
                  <button className="w-full text-left text-sm font-semibold text-green-400">Log In</button>
                )}
              </div>
          </div>
        </aside>

        {/* 4. MAIN CONTENT AREA */}
        {/* Removed 'h-screen' and 'overflow-y-auto' so mobile browsers handle scrolling naturally */}
       <main className="flex-1 w-full p-4 lg:p-8 overflow-x-hidden">
    <div className="max-w-[1600px] mx-auto">

        <ProjectContextBar
            filmData={filmData}
            activeTab={activeTab}
            allTabDetails={allTabDetails}
            tabs={tabs}
            onSwitchProject={() => handleTabSwitch('dashboard')}
        />
       
          
          {filmData && (
            <div className="relative">
              {/* NOTE: Each tab now controls its own scrolling behavior */}
              <div className={activeTab === 'dashboard' ? '' : 'hidden'}>
                <ProjectDashboard
                  projects={projects}
                  isLoading={isLoadingData}
                  onSelectProject={handleSelectProject}
                  saveFilmData={saveFilmData}
                  onDeleteProject={handleDeleteProject}
                  setNotification={setNotification}
                  user={user}
                  storage={storage}
                  onCreateNew={handleCreateNewProjectClick}
                  onUpdateProject={handleUpdateProject}
                />
              </div>
              <div className={activeTab === 'revenue' ? '' : 'hidden'}>
                <RevenueForecasterTab
                  key={filmData.id} // <--- FIX
                  filmData={filmData}
                  setNotification={setNotification}
                  saveFilmData={saveFilmData}
                  credits={credits}
                  deductCredit={deductCredit}
                />
              </div>
              <div className={activeTab === 'audience' ? '' : 'hidden'}>
                <AudienceAnalysisTab
                  key={filmData.id} // <--- FIX
                  filmData={filmData}
                  setFilmData={setFilmData}
                  setNotification={setNotification}
                  setConfirmModal={setConfirmModal}
                  saveFilmData={saveFilmData}
                  credits={credits}
                  deductCredit={deductCredit}
                />
              </div>
              <div className={activeTab === 'campaigns' ? '' : 'hidden'}>
                <CampaignManagerTab
                  key={filmData.id} // <--- FIX
                  filmData={filmData}
                  campaigns={campaigns}
                  addCampaign={addCampaign}
                  updateCampaign={updateCampaign}
                  deleteCampaign={deleteCampaign}
                  setNotification={setNotification}
                  openCampaignModal={openCampaignModal}
                />
              </div>
              <div className={activeTab === 'competition' ? '' : 'hidden'}>
                <CompetitiveIntelTab
                    key={filmData.id} // <--- FIX
                    filmData={filmData}
                    setNotification={setNotification}
                    saveFilmData={saveFilmData}
                    credits={credits}
                    deductCredit={deductCredit}
                />
              </div>
              <div className={activeTab === 'press' ? '' : 'hidden'}>
                <PressOutreachTab
                    key={filmData.id} // <--- FIX
                    filmData={filmData}
                    user={user}
                    db={db}
                    storage={storage}
                    saveFilmData={saveFilmData}
                    setNotification={setNotification}
                    influencerList={influencerList}
                    addInfluencer={addInfluencer}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    setActiveProject={setActiveProject}
                    saveFilmField={saveFilmField}
                    credits={credits}
                    deductCredit={deductCredit}
                  />
              </div>
              <div className={activeTab === 'video' ? '' : 'hidden'}>
                <VideoAnalysisTab
                   key={filmData.id} // <--- FIX
                   filmData={filmData}
                   setNotification={setNotification}
                   saveFilmData={saveFilmData}
                   db={db}
                   storage={storage}
                   credits={credits}
                   deductCredit={deductCredit}
                />
                </div>
                <div className={activeTab === 'visuals' ? '' : 'hidden'}>
                <VisualContentTab
                    key={filmData.id} // <--- FIX
                    filmData={filmData}
                    setNotification={setNotification}
                    saveFilmData={saveFilmData}
                    db={db}
                    storage={storage}
                    credits={credits}
                    deductCredit={deductCredit}
                />
                </div>
              <div className={activeTab === 'budget' ? '' : 'hidden'}>
                <BudgetPlannerTab
                    key={filmData.id} // <--- FIX
                    filmData={filmData}
                    setNotification={setNotification}
                    saveFilmData={saveFilmData}
                    credits={credits}
                    deductCredit={deductCredit}
                />
                </div>
              <div className={activeTab === 'content' ? '' : 'hidden'}>
                    <ContentCreationTab
                    key={filmData.id} // <--- FIX
                    filmData={filmData}
                    setNotification={setNotification}
                    openCampaignModal={openCampaignModal}
                    saveFilmData={saveFilmData}
                    campaigns={campaigns}
                    onSaveCampaign={handleSaveCampaign}
                    credits={credits}
                    deductCredit={deductCredit}
                    storage={storage}
                    />
                </div>
                <div className={activeTab === 'dossier' ? '' : 'hidden'}>
                <DossierTab
                    key={filmData.id} // <--- FIX
                    allData={allDataForDossier}
                    filmData={filmData}
                    setNotification={setNotification}
                    saveFilmData={saveFilmData}
                />
            </div>
              <div className={activeTab === 'analytics' ? '' : 'hidden'}>
                <AnalyticsTab
                  key={filmData.id} // <--- FIX
                  filmData={filmData}
                  campaigns={campaigns}
                  setNotification={setNotification}
                />
              </div>
              <div className={activeTab === 'simulator' ? '' : 'hidden'}>
                <AndromedaSimulatorTab
                  key={filmData.id}
                  filmData={filmData}
                  setNotification={setNotification}
                  credits={credits}
                  deductCredit={deductCredit}
                />
              </div>
              <div className={activeTab === 'community' ? '' : 'hidden'}>
                <CampaignIntelligenceTab
                  key={filmData.id} // <--- FIX
                  filmData={filmData}
                  setNotification={setNotification}
                  db={db}
                  user={user}
                  saveFilmData={saveFilmData}
                  credits={credits}
                  deductCredit={deductCredit}
                  storage={storage}
                />
              </div>
              <div className={`h-full w-full absolute top-0 left-0 ${activeTab === 'greenlight' ? '' : 'hidden'}`}>
    <GreenlightCoverageTab
    key={filmData.id} 
        filmData={filmData}
        saveFilmData={saveFilmData}
        setNotification={setNotification}
        credits={credits}
        deductCredit={deductCredit}
    />
</div>
              <div className={activeTab === 'remix' ? '' : 'hidden'}>
                <RemixTab
                  key={filmData.id} // <--- FIX
                  filmData={filmData}
                  setNotification={setNotification}
                  openCampaignModal={openCampaignModal}
                  saveFilmData={saveFilmData}
                  storage={storage}
                  db={db}
                  user={user}
                  credits={credits}
                  deductCredit={deductCredit}
                />
            </div>
              <div className={activeTab === 'settings' ? '' : 'hidden'}>
                <SettingsTab
                    user={user}
                    userProfile={{
                      ...fullUserProfile, 
                      isPro,              
                      creditsRemaining: credits,
                      monthlyLimit: maxCredits,
                      plan: isPro ? 'pro' : 'free'
                    }}
                    handleLogout={handleLogout}
                    setNotification={setNotification}
                    resetFilmData={resetFilmData}
                />
              </div>
            </div>
          )}
         </div>       {/* closes max-w-[1600px] */}
   </main>
      </div>
    </div>
  );
};

const AuthGate = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });

  useEffect(() => {
    // 1. BREAK THE STRIPE REDIRECT LOOP
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');

    if (paymentStatus) {
        sessionStorage.removeItem('blackbox_pending_checkout');
        sessionStorage.removeItem('blackbox_pending_plan_cycle');
        
        // Clean URL without reloading
        window.history.replaceState({}, document.title, window.location.pathname);

        if (paymentStatus === 'success') {
            setNotification({ type: 'success', message: 'Subscription activated! Welcome aboard.' });
        } else {
            setNotification({ type: 'info', message: 'Payment was canceled.' });
        }
    }

    if (!auth.app) initializeApp(firebaseConfig);

    // 2. LISTEN FOR AUTH STATE
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
          const isPendingCheckout = sessionStorage.getItem('blackbox_pending_checkout');
          
          if (isPendingCheckout && !paymentStatus) {
              setIsRedirecting(true);
              await handlePendingCheckoutRedirect(authUser);
          } else {
              setUser(authUser);
          }
      } else {
          setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleDismissNotification = () => setNotification({ message: '', type: '' });

  // RENDER: Loading State
  if (authLoading || isRedirecting) {
    return (
      <div className="dark font-poppins">
        <div className="bg-zinc-950 min-h-screen flex flex-col items-center justify-center">
          <BlackboxLoaderIcon className="w-24 h-24" />
          <h1 className="text-4xl font-bold text-white font-poppins mt-6">BLACKBOX</h1>
          <p className="text-zinc-400 animate-pulse">
            {isRedirecting ? 'Securing your plan...' : 'Initializing...'}
          </p>
        </div>
      </div>
    );
  }

  // RENDER: Landing Site (No User)
  if (!user) {
    return (
      <>
        <Notification message={notification.message} type={notification.type} onDismiss={handleDismissNotification} />
        <LandingSite setNotification={setNotification} auth={auth} />
      </>
    );
  }

  // RENDER: Main Dashboard (Authenticated)
  return (
    <App
      key={user.uid}
      user={user}
      db={db}
      storage={storage}
      auth={auth}
      notification={notification}
      setNotification={setNotification}
    />
  );
};

// --- MAIN ROUTER (DEFAULT EXPORT) ---
export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
         {/* Legal Pages */}
         <Route path="/privacy" element={<PrivacyPage />} />
         <Route path="/terms" element={<TermsPage />} />
         
         {/* Public Share Link */}
         <Route path="/share/:projectId" element={<PublicDossierWrapper db={db} />} />
         
         {/* Root Path (Landing or App) */}
         <Route path="/" element={<AuthGate />} />
         
         {/* Catch-all redirect to home */}
         <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}